// frontend/web/src/components/product/CreateProductForm.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Button, Typography, TextField, FormControl, InputLabel, Select,
  MenuItem, Grid, Paper, IconButton, Chip, Alert, AlertTitle, CircularProgress,
  Stepper, Step, StepLabel, Divider, FormHelperText, Card, CardContent,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
  Autocomplete, Avatar, Switch, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemButton, ListItemText, ListItemIcon, Collapse,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CloudUpload as CloudUploadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarTodayIcon,
  LocationOn as LocationOnIcon,
  Euro as EuroIcon,
  Inventory as InventoryIcon,
  Image as ImageIcon,
  Folder as FolderIcon,
  InsertDriveFile as InsertDriveFileIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  CheckCircle as CheckCircleIcon,
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Block as BlockIcon,
  PauseCircle as PauseCircleIcon,
  Archive as ArchiveIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { ar, enUS, fr } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCategories } from '../../context/CategoryContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getCurrencyByCountry } from '../../config/currencies';
import { getCurrencyForCountry } from '../../utils/currencyUtils';
import { useCategoryName, useCategoryDescription } from '../../utils/categoryTranslation';
import ListingMap from '../listing/ListingMap';
import ListingDetailsEditor, {
  normalizeListingDetailsValue,
  validateListingDetailsValues
} from '../listing/ListingDetailsEditor';
import geocodingService from '../../services/geocodingService';
import productService from '../../services/productService';
import { PRODUCT_CONDITION_OPTIONS } from '../../constants/productConditions';

// ─── Constantes ───────────────────────────────────────────────────────────────

const STEPS = ['basicInfo', 'details', 'location', 'delivery', 'pricing', 'availability', 'images'];

const WEEK_DAYS_BASE = [
  { day: 1, labelKey: 'weekDay.monday' },
  { day: 2, labelKey: 'weekDay.tuesday' },
  { day: 3, labelKey: 'weekDay.wednesday' },
  { day: 4, labelKey: 'weekDay.thursday' },
  { day: 5, labelKey: 'weekDay.friday' },
  { day: 6, labelKey: 'weekDay.saturday' },
  { day: 7, labelKey: 'weekDay.sunday' }
];

const PRODUCT_VARIANT_TYPES_BASE = [
  { value: 'color',         labelKey: 'variantType.color' },
  { value: 'size',          labelKey: 'variantType.size' },
  { value: 'weight',        labelKey: 'variantType.weight' },
  { value: 'material',      labelKey: 'variantType.material' },
  { value: 'style',         labelKey: 'variantType.style' },
  { value: 'flavor',        labelKey: 'variantType.flavor' },
  { value: 'scent',         labelKey: 'variantType.scent' },
  { value: 'format',        labelKey: 'variantType.format' },
  { value: 'capacity',      labelKey: 'variantType.capacity' },
  { value: 'length',        labelKey: 'variantType.length' },
  { value: 'width',         labelKey: 'variantType.width' },
  { value: 'height',        labelKey: 'variantType.height' },
  { value: 'diameter',      labelKey: 'variantType.diameter' },
  { value: 'finish',        labelKey: 'variantType.finish' },
  { value: 'pattern',       labelKey: 'variantType.pattern' },
  { value: 'edition',       labelKey: 'variantType.edition' },
  { value: 'version',       labelKey: 'variantType.version' },
  { value: 'configuration', labelKey: 'variantType.configuration' },
  { value: 'connectivity',  labelKey: 'variantType.connectivity' },
  { value: 'power',         labelKey: 'variantType.power' },
  { value: 'voltage',       labelKey: 'variantType.voltage' },
  { value: 'age_range',     labelKey: 'variantType.age_range' },
  { value: 'gender',        labelKey: 'variantType.gender' },
  { value: 'cut',           labelKey: 'variantType.cut' },
  { value: 'fitting',       labelKey: 'variantType.fitting' },
  { value: 'pack_size',     labelKey: 'variantType.pack_size' },
  { value: 'storage',       labelKey: 'variantType.storage' },
  { value: 'ram',           labelKey: 'variantType.ram' },
  { value: 'processor',     labelKey: 'variantType.processor' },
  { value: 'os',            labelKey: 'variantType.os' },
  { value: 'resolution',    labelKey: 'variantType.resolution' },
  { value: 'coating',       labelKey: 'variantType.coating' },
  { value: 'filling',       labelKey: 'variantType.filling' },
  { value: 'hardness',      labelKey: 'variantType.hardness' },
  { value: 'temperature',   labelKey: 'variantType.temperature' },
  { value: 'origin',        labelKey: 'variantType.origin' },
  { value: 'certification', labelKey: 'variantType.certification' },
  { value: 'compatibility', labelKey: 'variantType.compatibility' },
  { value: 'language',      labelKey: 'variantType.language' },
  { value: 'subscription',  labelKey: 'variantType.subscription' },
  { value: 'care',          labelKey: 'variantType.care' },
  { value: 'other',         labelKey: 'variantType.other' }
];

const UPLOADS_BASE_URL =
  process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const getImageUrl = (p) => {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${UPLOADS_BASE_URL}${p.startsWith('/') ? p : '/' + p}`;
};

// ─── Valeurs par défaut ───────────────────────────────────────────────────────

const DEFAULT_FORM = {
  title: '',
  description: '',
  condition: '',
  category_id: '',
  details: {},
  currency: 'EUR',
  preparation_time_minutes: 30,
  default_slot_duration_minutes: 60,
  slot_client_capacity: 1,
  country_code: ''
};

const parseDateString = (value) => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? startOfDay(parsed) : null;
};

/**
 * Helper pour détecter les chevauchements de périodes d'indisponibilité
 * @param {string} newStart - Date de début (yyyy-MM-dd)
 * @param {string} newEnd - Date de fin (yyyy-MM-dd)
 * @param {Array} existingPeriods - Périodes existantes [{ available_from, available_to, reason?, _deleted? }]
 * @returns {Object} { hasOverlap: boolean, conflictingPeriods: Array }
 */
const checkUnavailabilityOverlap = (newStart, newEnd, existingPeriods) => {
  if (!newStart || !newEnd) return { hasOverlap: false, conflictingPeriods: [] };
  
  const newStartDate = parseDateString(newStart);
  const newEndDate = parseDateString(newEnd);
  
  if (!newStartDate || !newEndDate) return { hasOverlap: false, conflictingPeriods: [] };
  
  const conflicts = existingPeriods.filter((period) => {
    // Skip deleted periods
    if (period._deleted) return false;
    
    const existingStart = parseDateString(period.available_from);
    const existingEnd = parseDateString(period.available_to);
    
    if (!existingStart || !existingEnd) return false;
    
    // Check for overlap: two periods overlap if one starts before the other ends
    return isWithinInterval(newStartDate, { start: existingStart, end: existingEnd }) ||
           isWithinInterval(newEndDate, { start: existingStart, end: existingEnd }) ||
           isWithinInterval(existingStart, { start: newStartDate, end: newEndDate }) ||
           isWithinInterval(existingEnd, { start: newStartDate, end: newEndDate });
  });
  
  return {
    hasOverlap: conflicts.length > 0,
    conflictingPeriods: conflicts
  };
};

// ─── Composant ───────────────────────────────────────────────────────────────

export default function CreateProductForm() {
  const { t: tRaw, i18n } = useTranslation(['products', 'listing']);
  // Wrapper sécurisé : garantit que t() retourne toujours une string.
  // i18next peut retourner un objet quand returnObjects est activé globalement
  // ou quand une clé pointe vers un objet imbriqué — ce qui crashe React.
  const t = (key, opts) => {
    const raw = tRaw(key, opts);
    if (raw === null || raw === undefined) return key;
    if (typeof raw === 'object') return opts?.defaultValue ? String(opts.defaultValue) : key;
    return String(raw);
  };
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { user } = useAuth();
  const { showNotification } = useNotifications();
  const { categoriesList = [], loading: categoriesLoading } = useCategories();
  const { getCurrencySelectOptions } = useCurrency();
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();
  const calendarLocale = i18n.language?.startsWith('ar') ? ar : i18n.language?.startsWith('en') ? enUS : fr;

  const isEditMode = Boolean(editId);

  // ── Options traductions ────────────────────────────────────────────────────
  const STEP_LABELS = {
    basicInfo: t('steps.basicInfo'),
    details: t('steps.details'),
    location: t('steps.location'),
    delivery: t('steps.delivery'),
    pricing: t('steps.pricing'),
    availability: t('steps.availability'),
    images: t('steps.images')
  };

  // Générer les jours de la semaine traduits
  const WEEK_DAYS = WEEK_DAYS_BASE.map((d) => ({
    day: d.day,
    label: t(d.labelKey)
  }));

  // Générer les types de variantes traduits
  const PRODUCT_VARIANT_TYPES = PRODUCT_VARIANT_TYPES_BASE.map((vt) => ({
    value: vt.value,
    label: t(vt.labelKey)
  }));

  const SLOT_DURATION_OPTIONS = [
    { value: 15,  label: t('slotDurations.15') },
    { value: 30,  label: t('slotDurations.30') },
    { value: 60,  label: t('slotDurations.60') },
    { value: 90,  label: t('slotDurations.90') },
    { value: 120, label: t('slotDurations.120') },
    { value: 180, label: t('slotDurations.180') }
  ];

  const PREP_TIME_OPTIONS = [
    { value: 0,     label: t('prepTimes.0') },
    { value: 15,    label: t('prepTimes.15') },
    { value: 30,    label: t('prepTimes.30') },
    { value: 60,    label: t('prepTimes.60') },
    { value: 120,   label: t('prepTimes.120') },
    { value: 240,   label: t('prepTimes.240') },
    { value: 480,   label: t('prepTimes.480') },
    { value: 1440,  label: t('prepTimes.1440') },
    { value: 2880,  label: t('prepTimes.2880') },
    { value: 4320,  label: t('prepTimes.4320') },
    { value: 7200,  label: t('prepTimes.7200') },
    { value: 10080, label: t('prepTimes.10080') }
  ];

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [pickupLocation, setPickupLocation] = useState(null); // { lat, lng, address }
  const [mapPosition, setMapPosition] = useState([48.8566, 2.3522]);
  const [addressLabel, setAddressLabel] = useState('');
  // Devise d'origine du produit en mode édition (immuable après chargement)
  const originalCurrencyRef = useRef(null);
  // Référence pour l'alerte d'erreur currency mismatch (pour auto-scroll)
  const currencyErrorAlertRef = useRef(null);
  // Références vers les champs en erreur pour l'auto-scroll général
  const fieldRefs = useRef({});
  const listingDetailsEditorRef = useRef(null);
  const listingDetailsDefinitionsRef = useRef([]);
  const listingDetailsStatusRef = useRef({ loading: false, fetchError: null });
  const [images, setImages] = useState([]); // { file?: File, preview: string, path?: string }
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [productId, setProductId] = useState(editId || null);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [variants, setVariants] = useState([]); // [{ type, options: [{value, price, weight_g, length_cm, width_cm, height_cm, quantity, sku}] }]
  const [openingHours, setOpeningHours] = useState([]); // [{ day: 1-7, from: 'HH:MM', to: 'HH:MM' }]
  const [unavailabilityPeriods, setUnavailabilityPeriods] = useState([]); // [{ available_from, available_to, reason }]
  const [newUnavailability, setNewUnavailability] = useState({ available_from: '', available_to: '', reason: '' });
  const [unavailabilityCalendarMonth, setUnavailabilityCalendarMonth] = useState(startOfMonth(new Date()));
  const [unavailabilitySelectionStart, setUnavailabilitySelectionStart] = useState(null);
  // État temporaire pour l’ajout d’une variante + ses options
  const [newVariantType, setNewVariantType] = useState('');
  const [newOption, setNewOption] = useState({ value: '', price: '', weight_g: '', length_cm: '', width_cm: '', height_cm: '', quantity: '', sku: '' });
  // { vIdx, oIdx } quand on est en mode edition d'une option existante, null sinon
  const [editingOption, setEditingOption] = useState(null);
  // 🆕 État pour la raison de rejet / archivage
  const [productStatus, setProductStatus] = useState(null); // 'rejected', 'archived', ou null
  const [rejectionReason, setRejectionReason] = useState(''); // Raison du rejet ou de l'archivage

  // ─── État options de livraison (Grille Tarifaire Dynamique) ───────────────
  const [deliveryOptions, setDeliveryOptions] = useState({
    enabled: false,
    standard: {
      pricing_tiers: [
        { min_quantity: 1, max_quantity: null, price_cents: 500 }
      ],
      days: 3
    },
    expressEnabled: false, // 🆕 Flag pour activer/désactiver Express
    express: {
      pricing_tiers: [
        { min_quantity: 1, max_quantity: null, price_cents: 1000 }
      ],
      days: 1
    }
  });

  // ─── Fonctions Helper pour Gérer les Paliers ──────────────────────────────
  const addTier = useCallback((type) => {
    setDeliveryOptions(prev => {
      const tiers = prev[type].pricing_tiers;
      const lastTier = tiers[tiers.length - 1];
      
      // Si le dernier palier n'a pas de max (illimité), ne rien faire
      if (lastTier.max_quantity === null) {
        return prev;
      }
      
      // Le nouveau palier commence obligatoirement à max_quantity + 1
      const newMinQuantity = lastTier.max_quantity + 1;
      
      return {
        ...prev,
        [type]: {
          ...prev[type],
          pricing_tiers: [
            ...tiers,
            { min_quantity: newMinQuantity, max_quantity: null, price_cents: lastTier.price_cents }
          ]
        }
      };
    });
  }, []);

  const removeTier = useCallback((type, index) => {
    setDeliveryOptions(prev => {
      const tiers = prev[type].pricing_tiers;
      if (tiers.length === 1) return prev; // Ne pas supprimer le dernier palier
      
      return {
        ...prev,
        [type]: {
          ...prev[type],
          pricing_tiers: tiers.filter((_, i) => i !== index)
        }
      };
    });
  }, []);

  const updateTier = useCallback((type, index, field, value) => {
    setDeliveryOptions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        pricing_tiers: prev[type].pricing_tiers.map((tier, i) =>
          i === index ? { ...tier, [field]: value } : tier
        )
      }
    }));
  }, []);

  const updateDeliveryDays = useCallback((type, days) => {
    setDeliveryOptions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        days: parseInt(days, 10) || (type === 'standard' ? 3 : 1)
      }
    }));
  }, []);

  // ─── Validation des Paliers de Tarification ───────────────────────────────
  const validateTiers = useCallback((tiers) => {
    const errors = [];
    
    // Tri par min_quantity pour faciliter la validation
    const sortedTiers = [...tiers].sort((a, b) => a.min_quantity - b.min_quantity);
    
    sortedTiers.forEach((tier, index) => {
      // 1. Validation intra-palier : min <= max
      if (tier.max_quantity !== null && tier.min_quantity > tier.max_quantity) {
        errors.push({
          index: index + 1,
          type: 'intra',
          message: t('delivery.tierValidation.maxLowerThanMin')
        });
      }
      
      // 2. Validation inter-paliers : pas de chevauchement ni de trou
      if (index > 0) {
        const prevTier = sortedTiers[index - 1];
        
        // Vérifier s'il y a un trou
        if (prevTier.max_quantity !== null && tier.min_quantity > prevTier.max_quantity + 1) {
          errors.push({
            index: index + 1,
            type: 'gap',
            message: t('delivery.tierValidation.gap', { prev: index, next: index + 1 })
          });
        }
        
        // Vérifier s'il y a un chevauchement
        if (prevTier.max_quantity !== null && tier.min_quantity <= prevTier.max_quantity) {
          errors.push({
            index: index + 1,
            type: 'overlap',
            message: t('delivery.tierValidation.overlap', { index })
          });
        }
      }
    });
    
    return errors;
  }, [t]);

  // ── Catégories filtrées (product ou both) ──────────────────────────────────
  const productCategories = useMemo(
    () => categoriesList.filter((c) => !c.context || c.context === 'product' || c.context === 'both'),
    [categoriesList]
  );

  useEffect(() => {
    if (!form.category_id || categoriesLoading) return;

    const hasAllowedCategory = productCategories.some(
      (category) => String(category.id) === String(form.category_id)
    );

    if (!hasAllowedCategory) {
      setForm((prev) => ({ ...prev, category_id: '' }));
    }
  }, [form.category_id, productCategories, categoriesLoading]);

  // ── Arbre de catégories ────────────────────────────────────────────────────
  const parentIdsWithChildren = useMemo(() => {
    const parentIds = new Set();
    for (const cat of productCategories) {
      const parentId = cat?.parent_id || cat?.parentId || cat?.parent?.id;
      if (parentId) parentIds.add(String(parentId));
    }
    return parentIds;
  }, [productCategories]);

  const productCategoryTreeRoots = useMemo(() => {
    const byId = new Map();
    for (const cat of productCategories) {
      if (!cat || cat.id === undefined || cat.id === null) continue;
      byId.set(String(cat.id), { ...cat, children: [] });
    }
    const roots = [];
    for (const node of byId.values()) {
      const parentId = node?.parent_id || node?.parentId || node?.parent?.id;
      const parentKey = parentId ? String(parentId) : null;
      if (parentKey && byId.has(parentKey)) {
        byId.get(parentKey).children.push(node);
      } else {
        roots.push(node);
      }
    }
    return roots;
  }, [productCategories]);

  const isLeafCategory = (category) => {
    if (!category) return false;
    const idKey = category.id !== undefined && category.id !== null ? String(category.id) : null;
    const hasChildrenInFlatList = idKey ? parentIdsWithChildren.has(idKey) : false;
    const hasChildrenInTree = Array.isArray(category.children) && category.children.length > 0;
    return !hasChildrenInFlatList && !hasChildrenInTree;
  };

  const getCategoryFullPath = useCallback((categoryId) => {
    const category = productCategories.find(cat => cat.id === categoryId);
    if (!category) return '';
    const buildPath = (cat, cache = new Map()) => {
      if (cache.has(cat.id)) return cache.get(cat.id);
      const parentId = cat.parent_id || cat.parentId || (cat.parent && cat.parent.id);
      let path;
      if (!parentId) {
        path = getCategoryName(cat);
      } else {
        const parent = productCategories.find(c => c.id === parentId);
        path = parent ? `${buildPath(parent, cache)} > ${getCategoryName(cat)}` : getCategoryName(cat);
      }
      cache.set(cat.id, path);
      return path;
    };
    return buildPath(category);
  }, [productCategories, getCategoryName]);

  const filterProductCategories = useCallback((categories, query) => {
    if (!query) return categories;
    return categories.filter(cat => {
      const path = getCategoryFullPath(cat.id);
      return path.toLowerCase().includes(query.toLowerCase()) ||
             getCategoryName(cat).toLowerCase().includes(query.toLowerCase());
    });
  }, [getCategoryFullPath, getCategoryName]);

  const toggleCategoryExpansion = (categoryId) => {
    const key = String(categoryId);
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const renderCategoryTree = (nodes, level = 0) => {
    return nodes.map((node) => {
      const isLeaf = isLeafCategory(node);
      const nodeKey = String(node.id);
      const isExpanded = expandedCategories.has(nodeKey);
      const isSelected = String(form.category_id) === String(node.id);
      const nodeImageUrl = getImageUrl(node.imageUrl);
      return (
        <Box key={node.id}>
          <ListItem disablePadding sx={{ pl: level * 3, mb: 0.5 }}>
            <ListItemButton
              selected={isSelected}
              onClick={() => {
                if (isLeaf) {
                  setForm(f => ({ ...f, category_id: node.id }));
                  setCategorySearchQuery('');
                } else {
                  toggleCategoryExpansion(nodeKey);
                }
              }}
              sx={{
                borderRadius: '8px', mx: 1, transition: 'all 0.3s ease-in-out',
                background: isSelected ? 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)' : 'transparent',
                color: isSelected ? 'white' : 'inherit',
                '&:hover': { background: isSelected ? 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' : 'linear-gradient(145deg, #ffffff 0%, #f0f0f0 100%)', transform: 'translateX(4px)' },
                '&.Mui-selected': { background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 62, color: isSelected ? 'white' : 'inherit' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isLeaf && (
                    <Box sx={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s', display: 'flex' }}>
                      <ChevronRightIcon fontSize="small" />
                    </Box>
                  )}
                  {nodeImageUrl ? (
                    <Avatar variant="rounded" src={nodeImageUrl} alt={getCategoryName(node)} sx={{ width: 28, height: 28, borderRadius: '8px' }} imgProps={{ loading: 'lazy' }} />
                  ) : isLeaf ? (
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: isSelected ? 'rgba(255,255,255,0.2)' : 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <InsertDriveFileIcon fontSize="small" sx={{ color: 'white', fontSize: '14px' }} />
                    </Box>
                  ) : (
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: isSelected ? 'rgba(255,255,255,0.2)' : 'linear-gradient(145deg, #f0f0f0 0%, #e0e0e0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FolderIcon fontSize="small" sx={{ color: isSelected ? 'white' : '#666', fontSize: '14px' }} />
                    </Box>
                  )}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={String(getCategoryName(node) ?? '')}
                primaryTypographyProps={{ fontWeight: isSelected ? 600 : (isLeaf ? 500 : 600), color: isSelected ? 'white' : (isLeaf ? 'text.primary' : 'text.secondary'), fontSize: isLeaf ? '0.875rem' : '0.9rem' }}
              />
              {isSelected && <CheckCircleIcon fontSize="small" sx={{ color: 'white', ml: 1 }} />}
            </ListItemButton>
          </ListItem>
          {!isLeaf && node.children && node.children.length > 0 && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderCategoryTree(node.children, level + 1)}
            </Collapse>
          )}
        </Box>
      );
    });
  };

  // ── Chargement en mode édition ─────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode) return;
    const fetchProduct = async () => {
      try {
        const res = await productService.getById(editId);
        const p = res.data;
        
        // 🆕 Charger le statut et la raison du rejet (coercion String pour éviter crash React si l'API renvoie un objet)
        if (p.status != null) setProductStatus(String(p.status));
        if (p.rejection_reason) {
          setRejectionReason(typeof p.rejection_reason === 'string' ? p.rejection_reason : JSON.stringify(p.rejection_reason));
        }
        
        setForm({
          title: p.title || '',
          description: p.description || '',
          condition: p.condition || '',
          category_id: p.category_id || '',
          details: normalizeListingDetailsValue(p.details),
          currency: p.currency || 'EUR',
          preparation_time_minutes: p.preparation_time_minutes || 30,
          default_slot_duration_minutes: p.default_slot_duration_minutes || 60,
          slot_client_capacity: p.slot_client_capacity || 1,
          country_code: p.country_code || ''
        });
        // Stocker la devise d'origine pour la validation en mode édition
        originalCurrencyRef.current = p.currency || 'EUR';
        // Convertir price_cents → price (string décimal) dans chaque option de variante
        // IMPORTANT : ne PAS faire ...opt pour éviter que price_cents reste dans le state.
        // À la soumission, normalizeVariants lit price_cents en priorité sur price —
        // si price_cents est présent, la nouvelle valeur price serait ignorée.
        if (Array.isArray(p.variants) && p.variants.length > 0) {
          setVariants(p.variants.map((v) => ({
            type: v.type,
            options: (v.options || []).map((opt) => ({
              value:     opt.value,
              price:     opt.price_cents !== undefined
                           ? (opt.price_cents / 100).toFixed(2)
                           : String(opt.price || ''),
              weight_g:  opt.weight_g,
              length_cm: opt.length_cm,
              width_cm:  opt.width_cm,
              height_cm: opt.height_cm,
              quantity:  opt.quantity,
              ...(opt.sku ? { sku: opt.sku } : {})
            }))
          })));
        }
        if (Array.isArray(p.opening_hours)) {
          setOpeningHours(p.opening_hours);
        }
        // Charger les periodes depuis la reponse incluse dans le produit
        if (Array.isArray(p.unavailabilityPeriods) && p.unavailabilityPeriods.length > 0) {
          setUnavailabilityPeriods(p.unavailabilityPeriods.map((u) => ({
            id: u.id,
            available_from: u.available_from,
            available_to: u.available_to,
            reason: u.reason || ''
          })));
        } else {
          // Fallback : appel explicite de l'endpoint si non inclus
          try {
            const perRes = await productService.listUnavailabilityPeriods(editId);
            const periods = perRes?.data || [];
            setUnavailabilityPeriods(periods.map((u) => ({
              id: u.id,
              available_from: u.available_from,
              available_to: u.available_to,
              reason: u.reason || ''
            })));
          } catch (_) { /* silencieux */ }
        }
        if (p.pickup_location) {
          setPickupLocation(p.pickup_location);
          setMapPosition([p.pickup_location.lat, p.pickup_location.lng]);
          setAddressLabel(p.pickup_location.address || '');
        }
        if (p.images?.length) {
          setImages(p.images.map((path) => ({ path, preview: getImageUrl(path) })));
        }
        
        // ─── Charger options de livraison (Grille Tarifaire) ───────────────────
        if (p.meta?.delivery_enabled) {
          const config = p.meta.delivery_config;
          
          // Déterminer si Express est activé (si des paliers existent OU si l'ancien système avait un prix express)
          const hasExpressTiers = config.express?.pricing_tiers && config.express.pricing_tiers.length > 0;
          const hasExpressOldSystem = config.express_price_cents !== undefined && config.express_price_cents !== null;
          
          setDeliveryOptions({
            enabled: true,
            standard: {
              pricing_tiers: config.standard?.pricing_tiers || [
                { min_quantity: 1, max_quantity: null, price_cents: config.standard_price_cents || 500 }
              ],
              days: config.standard?.days || config.standard_days || 3
            },
            expressEnabled: hasExpressTiers || hasExpressOldSystem, // 🆕 Activer Express uniquement si configuré
            express: {
              pricing_tiers: config.express?.pricing_tiers || (hasExpressOldSystem ? [
                { min_quantity: 1, max_quantity: null, price_cents: config.express_price_cents || 1000 }
              ] : [
                { min_quantity: 1, max_quantity: null, price_cents: 1000 }
              ]),
              days: config.express?.days || config.express_days || 1
            }
          });
        }
        // ───────────────────────────────────────────────────────────────────
      } catch (err) {
        showNotification('Impossible de charger le produit', 'error');
        navigate('/my-products');
      } finally {
        setInitialLoading(false);
      }
    };
    fetchProduct();
  }, [editId, isEditMode]);

  // Auto-scroll vers l'erreur currency mismatch quand elle apparaît
  useEffect(() => {
    if (errors.pickup_location) {
      const timer = setTimeout(() => {
        if (currencyErrorAlertRef.current) {
          const rect = currencyErrorAlertRef.current.getBoundingClientRect();
          window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - 100), behavior: 'smooth' });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [errors.pickup_location]);

  // ── Gestion carte ──────────────────────────────────────────────────────────
  const handleMapPositionChange = useCallback(async ({ latitude, longitude }) => {
    setPickupLocation({ lat: latitude, lng: longitude, address: addressLabel });
    setMapPosition([latitude, longitude]);
    // Reverse géocode + extraction du pays
    try {
      const result = await geocodingService.reverseGeocode(latitude, longitude);
      const addr = result?.display_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      setAddressLabel(addr);
      setPickupLocation({ lat: latitude, lng: longitude, address: addr });
      // Extraire le code pays depuis l'adresse inversée
      const countryCode = result?.address?.country_code
        ? result.address.country_code.toUpperCase()
        : '';
      if (countryCode) {
        setForm((f) => ({ ...f, country_code: countryCode }));

        // En mode édition : vérifier la compatibilité devise
        if (isEditMode && originalCurrencyRef.current) {
          const locationCurrency = getCurrencyByCountry(countryCode);
          if (locationCurrency && locationCurrency !== originalCurrencyRef.current) {
            setErrors((prev) => ({
              ...prev,
              pickup_location: t('form.currencyMismatch', {
                from: originalCurrencyRef.current,
                to: locationCurrency
              })
            }));
          } else {
            setErrors((prev) => {
              const newErr = { ...prev };
              delete newErr.pickup_location;
              return newErr;
            });
          }
        }
      }
    } catch (_) {
      setAddressLabel(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
    }
  }, [addressLabel, isEditMode, t]);

  // ── Synchronisation devise / pays ─────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode && form.country_code) {
      const countryCurrency = getCurrencyByCountry(form.country_code);
      if (countryCurrency && countryCurrency !== form.currency) {
        setForm((f) => ({ ...f, currency: countryCurrency }));
      }
    }
  }, [form.country_code, isEditMode]);

  useEffect(() => {
    const syncCurrencyByCountry = async () => {
      if (form.country_code) {
        try {
          const currencyInfo = await getCurrencyForCountry(form.country_code);

          // En mode édition : valider la compatibilité sans modifier la devise
          if (isEditMode && originalCurrencyRef.current) {
            if (currencyInfo?.code && currencyInfo.code !== originalCurrencyRef.current) {
              setErrors((prev) => ({
                ...prev,
                pickup_location: t('form.currencyMismatch', {
                  from: originalCurrencyRef.current,
                  to: currencyInfo.code
                })
              }));
            } else {
              setErrors((prev) => {
                const newErr = { ...prev };
                delete newErr.pickup_location;
                return newErr;
              });
            }
            return;
          }

          if (currencyInfo?.code && currencyInfo.code !== form.currency) {
            setForm((f) => ({ ...f, currency: currencyInfo.code }));
          }
        } catch (_) { /* silencieux */ }
      }
    };
    const timerId = setTimeout(syncCurrencyByCountry, 500);
    return () => clearTimeout(timerId);
  }, [form.country_code]);

  // ── Gestion images ─────────────────────────────────────────────────────────
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages((prev) => [...prev, ...newImages].slice(0, 10));
  };

  const handleImageRemove = (index) => {
    setImages((prev) => {
      const updated = [...prev];
      if (updated[index]?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  /**
   * Définir l'image à targetIndex comme photo principale (index 0).
   * Swappe simplement avec l'image en position 0.
   */
  const handleSetMainImage = (targetIndex) => {
    if (targetIndex === 0) return;
    setImages((prev) => {
      const updated = [...prev];
      [updated[0], updated[targetIndex]] = [updated[targetIndex], updated[0]];
      return updated;
    });
  };

  // ── Scroll vers le premier champ en erreur ────────────────────────────────
  const scrollToFirstError = useCallback((errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    // Pour pickup_location : utiliser l'Alert ref (Currency mismatch ou localisation manquante)
    const el =
      firstKey === 'pickup_location' && currencyErrorAlertRef.current
        ? currencyErrorAlertRef.current
        : fieldRefs.current[firstKey] || null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - 100), behavior: 'smooth' });
  }, []);

  const validateDetailsSection = useCallback((options = {}) => {
    if (!form.category_id) {
      return { isValid: true, errors: {}, firstErrorKey: null, reason: null };
    }

    const imperativeValidation = listingDetailsEditorRef.current?.validateAllFields?.({
      shouldScroll: options.shouldScroll
    });

    const validationResult = imperativeValidation || validateListingDetailsValues({
      fieldDefs: listingDetailsDefinitionsRef.current,
      details: form.details,
      t,
      loading: listingDetailsStatusRef.current.loading,
      fetchError: listingDetailsStatusRef.current.fetchError
    });

    if (!validationResult.isValid && options.shouldScroll !== false) {
      setTimeout(() => {
        listingDetailsEditorRef.current?.scrollToFirstError?.(validationResult.firstErrorKey);
      }, 120);
    }

    return validationResult;
  }, [form.category_id, form.details, t]);

  const buildStepErrors = useCallback((stepKey) => {
    const errs = {};

    if (stepKey === 'basicInfo') {
      if (!form.title.trim()) errs.title = t('form.titleRequired');
      if (form.title.trim().length < 3) errs.title = t('form.titleMinLength');
      if (!form.description.trim()) errs.description = t('form.descriptionRequired');
      if (!form.condition) errs.condition = t('form.conditionRequired');
      if (!form.category_id) errs.category_id = t('form.categoryRequired');
    }

    if (stepKey === 'location') {
      if (!pickupLocation) errs.pickup_location = t('form.pickupLocationRequired');
      if (isEditMode && originalCurrencyRef.current && form.country_code) {
        const locationCurrency = getCurrencyByCountry(form.country_code);
        if (locationCurrency && locationCurrency !== originalCurrencyRef.current) {
          errs.pickup_location = t('form.currencyMismatch', {
            from: originalCurrencyRef.current,
            to: locationCurrency
          });
        }
      }
    }

    if (stepKey === 'delivery') {
      // Si la livraison est activée, valider la structure des paliers
      if (deliveryOptions.enabled) {
        // Valider les paliers Standard (obligatoire)
        if (!deliveryOptions.standard.pricing_tiers || deliveryOptions.standard.pricing_tiers.length === 0) {
          errs.delivery_standard_tiers = t('form.variantMinRequired');
        } else {
          // Valider chaque palier standard
          const tierErrors = validateTiers(deliveryOptions.standard.pricing_tiers);
          if (tierErrors.length > 0) {
            errs.delivery_standard_tiers = tierErrors.map(e => e.message).join(', ');
          }
        }
        
        // Valider les jours de livraison standard
        if (!deliveryOptions.standard.days || deliveryOptions.standard.days < 1) {
          errs.delivery_standard_days = t('form.variantOptionRequired');
        }
        
        // Valider Express UNIQUEMENT s'il est activé
        if (deliveryOptions.expressEnabled) {
          if (!deliveryOptions.express.pricing_tiers || deliveryOptions.express.pricing_tiers.length === 0) {
            errs.delivery_express_tiers = t('form.variantMinRequired');
          } else {
            const expressErrors = validateTiers(deliveryOptions.express.pricing_tiers);
            if (expressErrors.length > 0) {
              errs.delivery_express_tiers = expressErrors.map(e => e.message).join(', ');
            }
          }
          
          // Valider les jours Express
          if (!deliveryOptions.express.days || deliveryOptions.express.days < 1) {
            errs.delivery_express_days = t('form.variantOptionRequired');
          }
        }
      }
    }

    if (stepKey === 'pricing') {
      if (variants.length === 0) {
        errs.variants = t('form.variantMinRequired');
      } else if (variants.every((variant) => variant.options.length === 0)) {
        errs.variants = t('form.variantOptionMinRequired');
      }
    }

    return errs;
  }, [form.title, form.description, form.condition, form.category_id, form.country_code, pickupLocation, isEditMode, variants, deliveryOptions, t]);

  // ── Validation par étape ───────────────────────────────────────────────────
  // Retourne l'objet d'erreurs (vide = valid) pour permettre le scroll immédiat
  const validateStep = useCallback((step, options = {}) => {
    const stepKey = typeof step === 'number' ? STEPS[step] : step;

    if (stepKey === 'details') {
      return {
        ...validateDetailsSection(options),
        stepKey
      };
    }

    const errs = buildStepErrors(stepKey);

    if (options.persistErrors !== false) {
      setErrors(errs);
    }

    return {
      isValid: Object.keys(errs).length === 0,
      errors: errs,
      firstErrorKey: Object.keys(errs)[0] || null,
      reason: Object.keys(errs).length > 0 ? 'fields' : null,
      stepKey
    };
  }, [buildStepErrors, validateDetailsSection]);

  const handleNext = () => {
    const validation = validateStep(activeStep, { shouldScroll: false });
    if (validation.isValid) {
      setActiveStep((s) => s + 1);
      // Scroll vers le haut du nouveau step
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Si on va à l'étape "delivery" et que la livraison est activée, autofocus le premier champ
      if (STEPS[activeStep + 1] === 'delivery' && deliveryOptions.enabled) {
        setTimeout(() => {
          const firstField = document.querySelector('input[name="delivery-standard-price"]');
          if (firstField) {
            firstField.focus();
          }
        }, 150);
      }
    } else {
      // Attendre que React rende les éléments d'erreur avant de scroller
      setTimeout(() => {
        if (validation.stepKey === 'details') {
          listingDetailsEditorRef.current?.scrollToFirstError?.(validation.firstErrorKey);
          return;
        }

        // Si erreur sur delivery et livraison activée, scroll + autofocus
        if (validation.stepKey === 'delivery' && deliveryOptions.enabled && validation.errors) {
          scrollToFirstError(validation.errors);
          // Autofocus le premier champ en erreur
          setTimeout(() => {
            const firstErrorField = document.querySelector('input[name="delivery-standard-price"]');
            if (firstErrorField && !deliveryOptions.standardPrice) {
              firstErrorField.focus();
            }
          }, 150);
        } else {
          scrollToFirstError(validation.errors);
        }
      }, 100);
    }
  };

  const handleBack = () => {
    setActiveStep((s) => s - 1);
    // Scroll vers le haut
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectedUnavailabilityStart = parseDateString(newUnavailability.available_from);
  const selectedUnavailabilityEnd = parseDateString(newUnavailability.available_to);
  const today = startOfDay(new Date());

  const unavailabilityWeekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language || 'fr', { weekday: 'short' });
    const mondayReference = new Date(2024, 0, 1);

    return Array.from({ length: 7 }, (_, index) => {
      const label = formatter.format(addDays(mondayReference, index)).replace('.', '');
      return label.charAt(0).toUpperCase() + label.slice(1);
    });
  }, [i18n.language]);

  const unavailabilityCalendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(unavailabilityCalendarMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(unavailabilityCalendarMonth), { weekStartsOn: 1 });
    const days = [];
    let cursor = start;

    while (cursor <= end) {
      days.push(cursor);
      cursor = addDays(cursor, 1);
    }

    return days;
  }, [unavailabilityCalendarMonth]);

  const selectedUnavailabilityDayCount = selectedUnavailabilityStart && selectedUnavailabilityEnd
    ? differenceInCalendarDays(selectedUnavailabilityEnd, selectedUnavailabilityStart) + 1
    : selectedUnavailabilityStart
      ? 1
      : 0;

  const isExistingUnavailabilityDay = useCallback((day) => {
    return unavailabilityPeriods.some((period) => {
      // Skip periods marked as deleted
      if (period._deleted) return false;
      
      const start = parseDateString(period.available_from);
      const end = parseDateString(period.available_to);

      if (!start || !end) return false;

      return isWithinInterval(day, { start, end });
    });
  }, [unavailabilityPeriods]);

  const clearUnavailabilitySelection = useCallback(() => {
    setUnavailabilitySelectionStart(null);
    setNewUnavailability((prev) => ({
      ...prev,
      available_from: '',
      available_to: ''
    }));
  }, []);

  const handleUnavailabilityDateClick = useCallback((day) => {
    const normalizedDay = startOfDay(day);

    if (isBefore(normalizedDay, today)) return;
    
    // Prevent selection on days that already have an existing unavailability period
    if (isExistingUnavailabilityDay(normalizedDay)) return;

    if (!unavailabilitySelectionStart || (selectedUnavailabilityStart && selectedUnavailabilityEnd)) {
      setUnavailabilitySelectionStart(normalizedDay);
      setNewUnavailability((prev) => ({
        ...prev,
        available_from: format(normalizedDay, 'yyyy-MM-dd'),
        available_to: ''
      }));
      return;
    }

    const rangeStart = isBefore(normalizedDay, unavailabilitySelectionStart)
      ? normalizedDay
      : unavailabilitySelectionStart;
    const rangeEnd = isBefore(normalizedDay, unavailabilitySelectionStart)
      ? unavailabilitySelectionStart
      : normalizedDay;

    setNewUnavailability((prev) => ({
      ...prev,
      available_from: format(rangeStart, 'yyyy-MM-dd'),
      available_to: format(rangeEnd, 'yyyy-MM-dd')
    }));
    setUnavailabilitySelectionStart(null);
  }, [selectedUnavailabilityEnd, selectedUnavailabilityStart, today, unavailabilitySelectionStart, isExistingUnavailabilityDay]);

  // ── Soumission ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const stepsToValidate = ['basicInfo', 'details', 'location', 'delivery', 'pricing'];
    const validations = stepsToValidate.map((stepKey) => validateStep(stepKey, { persistErrors: false, shouldScroll: false }));
    const firstInvalidStep = validations.find((validation) => !validation.isValid);

    if (firstInvalidStep) {
      setActiveStep(STEPS.indexOf(firstInvalidStep.stepKey));

      if (firstInvalidStep.stepKey === 'details') {
        setTimeout(() => {
          listingDetailsEditorRef.current?.scrollToFirstError?.(firstInvalidStep.firstErrorKey);
        }, 140);
      } else {
        setErrors(firstInvalidStep.errors);
        setTimeout(() => scrollToFirstError(firstInvalidStep.errors), 140);
      }

      return false;
    }

    setLoading(true);
    setSubmitting(true);
    setSubmissionMessage(isEditMode ? t('form.overlay.updating', 'Mise à jour du produit...') : t('form.overlay.creating', 'Création du produit...'));
    
    try {
      // Prix de base = min des prix de variantes (pour indexation / tri)
      const allPrices = variants.flatMap((v) =>
        v.options.map((o) => Number(o.price)).filter((n) => !isNaN(n) && n >= 0)
      );
      const basePrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
      // Quantité totale = somme des quantités de toutes les options
      const totalQuantity = variants.reduce(
        (sum, v) => sum + v.options.reduce((s, o) => s + (Number(o.quantity) || 0), 0),
        0
      );

      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description || '');
      formData.append('condition', form.condition);
      formData.append('category_id', form.category_id || '');
      formData.append('details', JSON.stringify(normalizeListingDetailsValue(form.details)));
      formData.append('price', String(basePrice));      // calculé depuis les variantes
      formData.append('currency', form.currency);
      formData.append('quantity', String(totalQuantity)); // calculé depuis les variantes
      formData.append('preparation_time_minutes', String(form.preparation_time_minutes));
      formData.append('default_slot_duration_minutes', String(form.default_slot_duration_minutes));
      formData.append('slot_client_capacity', String(form.slot_client_capacity || 1));
      if (form.country_code) formData.append('country_code', form.country_code);
      if (variants.length > 0) formData.append('variants', JSON.stringify(variants));
      if (openingHours.length > 0) formData.append('opening_hours', JSON.stringify(openingHours));
      if (pickupLocation) formData.append('pickup_location', JSON.stringify(pickupLocation));

      // ─── Encoder meta avec options de livraison (Grille Tarifaire) ────────────
      const metaData = {
        delivery_enabled: deliveryOptions.enabled,
        delivery_config: deliveryOptions.enabled ? {
          standard: deliveryOptions.standard,
          ...(deliveryOptions.expressEnabled ? { express: deliveryOptions.express } : {}) // 🆕 N'inclure Express que s'il est activé
        } : null
      };
      formData.append('meta', JSON.stringify(metaData));
      // ────────────────────────────────────────────────────────────────────────

      // Images : construire existing_images, new files et image_order selon l'ordre du tableau
      const existingPaths = [];
      const newFiles = [];
      const imageOrder = [];

      images.forEach((img) => {
        if (img.path && !img.file) {
          const idx = existingPaths.length;
          existingPaths.push(img.path);
          imageOrder.push(`e:${idx}`);
        } else if (img.file) {
          const idx = newFiles.length;
          newFiles.push(img.file);
          imageOrder.push(`n:${idx}`);
        }
      });
      
      // Mise à jour du message si on a de nouvelles images
      if (newFiles.length > 0) {
        setSubmissionMessage(t('form.overlay.preparingImages', 'Préparation des images...'));
      }

      if (existingPaths.length > 0) {
        formData.append('existing_images', JSON.stringify(existingPaths));
      }
      newFiles.forEach((file) => {
        formData.append('images', file);
      });
      if (imageOrder.length > 0) {
        formData.append('image_order', JSON.stringify(imageOrder));
      }

      let savedId = productId;
      if (isEditMode && productId) {
        // Lors d'une modification, forcer le statut à "pending" pour une nouvelle validation
        setSubmissionMessage(t('form.overlay.saving', 'Enregistrement...'));
        formData.append('status', 'pending');
        await productService.update(productId, formData);
        showNotification(t('form.successUpdate'), 'success');
      } else {
        setSubmissionMessage(t('form.overlay.sending', 'Envoi au serveur...'));
        const res = await productService.create(formData);
        savedId = res.data?.id;
        setProductId(savedId);
        showNotification(t('form.successCreate'), 'success');
        if (activeStep < STEPS.indexOf('availability')) {
          setActiveStep(STEPS.indexOf('availability'));
        }
      }

      // Handle unavailability periods: delete marked periods and create new local ones
      if (savedId) {
        setSubmissionMessage(t('form.overlay.savingAvailability', 'Enregistrement des disponibilités...'));
        
        // First, delete periods marked as _deleted
        const deletedPeriods = unavailabilityPeriods.filter((p) => p._deleted && p.id);
        if (deletedPeriods.length > 0) {
          await Promise.all(
            deletedPeriods.map(async (p) => {
              try {
                await productService.deleteUnavailabilityPeriod(p.id);
              } catch (err) {
                console.error('Erreur suppression période:', err);
              }
            })
          );
        }

        // Then, create new local periods
        const pendingPeriods = unavailabilityPeriods.filter((p) => p._local && !p._deleted);
        if (pendingPeriods.length > 0) {
          const saved = await Promise.all(
            pendingPeriods.map(async (p) => {
              const { _local, _deleted, ...data } = p;
              try {
                const r = await productService.createUnavailabilityPeriod(savedId, data);
                return { ...data, id: r.data?.id };
              } catch (_) { return { ...data }; }
            })
          );
          // Replace _local entries with persisted ones (with id) and remove _deleted entries
          setUnavailabilityPeriods((prev) =>
            prev.filter((p) => !p._local && !p._deleted).concat(saved)
          );
        } else {
          // Just remove _deleted entries if no new periods to create
          setUnavailabilityPeriods((prev) => prev.filter((p) => !p._deleted));
        }
      }

      return true;
    } catch (err) {
      const msg = err?.response?.data?.message || t('form.errorCreate');
      showNotification(msg, 'error');
      return false;
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    // Le bouton principal (bleu) doit toujours persister avant de quitter la page.
    // En mode édition, la version précédente naviguait sans sauvegarder.
    const ok = await handleSubmit();
    if (ok) navigate('/my-products');
  };

  // Vérifier s'il y a des erreurs pertinentes à l'étape actuelle
  const hasErrorsInCurrentStep = useCallback(() => {
    switch(STEPS[activeStep]) {
      case 'basicInfo':
        return !!errors.title || !!errors.description || !!errors.condition || !!errors.category_id;
      case 'details':
        return !validateDetailsSection({ shouldScroll: false }).isValid;
      case 'location':
        return !!errors.pickup_location;
      case 'pricing':
        return !!errors.variants;
      default:
        return false;
    }
  }, [errors, activeStep, validateDetailsSection]);

  if (initialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderStep = () => {
    switch (STEPS[activeStep]) {
      // ── Étape 0: Infos de base ─────────────────────────────────────────────
      case 'basicInfo':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label={t('form.title') + ' *'}
                fullWidth
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                error={Boolean(errors.title)}
                helperText={errors.title ? String(errors.title) : undefined}
                inputProps={{ maxLength: 255 }}
                inputRef={(el) => { fieldRefs.current.title = el; }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('form.description')+ ' *'}
                fullWidth
                multiline
                minRows={4}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                error={Boolean(errors.description)}
                helperText={errors.description ? String(errors.description) : undefined}
                inputRef={(el) => { fieldRefs.current.description = el; }}
              />
            </Grid>
            <Grid item xs={12} md={6} ref={(el) => { fieldRefs.current.condition = el; }}>
              <FormControl fullWidth error={Boolean(errors.condition)}>
                <InputLabel id="product-condition-label">{t('form.condition')}</InputLabel>
                <Select
                  labelId="product-condition-label"
                  value={form.condition}
                  label={t('form.condition')}
                  onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}
                >
                  {PRODUCT_CONDITION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.translationKey)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.condition ? String(errors.condition) : t('form.conditionHelper')}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} ref={(el) => { fieldRefs.current.category_id = el; }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                {t('form.category') + ' *'}
              </Typography>

              {/* Autocomplete avec recherche */}
              <Autocomplete
                fullWidth
                options={filterProductCategories(productCategories.filter(isLeafCategory), categorySearchQuery)}
                getOptionLabel={(option) => getCategoryFullPath(option.id)}
                value={productCategories.find(cat => cat.id === form.category_id) || null}
                onChange={(_, value) => {
                  setForm(f => ({ ...f, category_id: value ? value.id : '' }));
                  setCategorySearchQuery('');
                }}
                onInputChange={(_, newVal) => setCategorySearchQuery(newVal)}
                loading={categoriesLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    '&.Mui-focused': { boxShadow: '0 4px 20px rgba(98, 0, 238, 0.2)' },
                    ...(errors.category_id && {
                      borderColor: '#d32f2f',
                      '& fieldset': { borderColor: '#d32f2f' }
                    })
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('form.categorySearch')}
                    placeholder={t('form.categorySearchPlaceholder')}
                    error={Boolean(errors.category_id)}
                    helperText={errors.category_id ? String(errors.category_id) : undefined}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {categoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const optionImageUrl = getImageUrl(option.imageUrl);
                  return (
                    <li {...props} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderRadius: '8px', margin: '2px 8px' }}>
                      {optionImageUrl ? (
                        <Avatar variant="rounded" src={optionImageUrl} alt={String(getCategoryName(option) ?? '')} sx={{ width: 32, height: 32, borderRadius: '10px', mr: 2 }} imgProps={{ loading: 'lazy' }} />
                      ) : (
                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                          <InsertDriveFileIcon fontSize="small" sx={{ color: 'white' }} />
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {String(getCategoryName(option) ?? '')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', opacity: 0.8 }}>
                          {String(getCategoryFullPath(option.id) ?? '')}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={t('form.noCategoriesFound')}
              />

              {/* Catégorie sélectionnée */}
              {form.category_id && (() => {
                const selectedCategory = productCategories.find(cat => String(cat.id) === String(form.category_id));
                if (!selectedCategory) return null;
                const selectedImageUrl = getImageUrl(selectedCategory.imageUrl);
                const selectedTitle = getCategoryName(selectedCategory);
                const selectedDescription = getCategoryDescription(selectedCategory);
                return (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={String(getCategoryFullPath(form.category_id) ?? '')}
                      color="primary"
                      variant="filled"
                      onDelete={() => setForm(f => ({ ...f, category_id: '' }))}
                      sx={{
                        maxWidth: '100%', height: 'auto', p: 1,
                        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                        color: 'white', fontWeight: 500, borderRadius: '12px',
                        '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word' },
                        '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }
                      }}
                    />
                    <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        {selectedImageUrl && (
                          <Box component="img" src={selectedImageUrl} alt={selectedTitle}
                            sx={{ width: { xs: '100%', sm: 220 }, height: { xs: 160, sm: 140 }, objectFit: 'cover', borderRadius: 2, backgroundColor: 'background.default' }}
                            loading="lazy"
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 240 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{String(selectedTitle ?? '')}</Typography>
                          {selectedDescription && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{String(selectedDescription)}</Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                );
              })()}

              {/* Accordéon de navigation hiérarchique */}
              <Box sx={{ mt: 3 }}>
                <Accordion defaultExpanded sx={{ border: '2px solid #e0e0e0', borderRadius: '12px !important', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, '&:hover': { boxShadow: '0 4px 20px rgba(98, 0, 238, 0.1)', borderColor: '#6200ee' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{ background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white', minHeight: '56px !important', '&.Mui-expanded': { minHeight: '56px !important' }, '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' } }}
                  >
                    <Box display="flex" alignItems="center">
                      <FolderIcon sx={{ mr: 2 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t('form.orBrowseCategories')}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, backgroundColor: '#fafafa' }}>
                    <Box sx={{ maxHeight: 400, overflowY: 'auto', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', borderRadius: '3px' } }}>
                      {categoriesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress size={32} sx={{ color: '#6200ee' }} />
                        </Box>
                      ) : productCategoryTreeRoots.length > 0 ? (
                        <List dense sx={{ p: 1 }}>
                          {renderCategoryTree(productCategoryTreeRoots)}
                        </List>
                      ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                          <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="body2" color="text.secondary">{t('form.noCategoriesAvailable')}</Typography>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>
          </Grid>
        );

      case 'details':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                {t('listing:details.description', 'Ajoutez les détails spécifiques qui aident les acheteurs à comprendre précisément votre produit.')}
              </Alert>
            </Grid>

            {!form.category_id ? (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {t('form.categoryRequired')}
                </Alert>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <ListingDetailsEditor
                  ref={listingDetailsEditorRef}
                  offerType="product"
                  value={form.details}
                  onChange={(details) => {
                    setForm((previous) => ({
                      ...previous,
                      details
                    }));
                  }}
                  onDefinitionsChange={(definitions) => {
                    listingDetailsDefinitionsRef.current = definitions;
                  }}
                  onStatusChange={(status) => {
                    listingDetailsStatusRef.current = status;
                  }}
                />
              </Grid>
            )}
          </Grid>
        );

      // ── Étape 2: Prix, stock, variantes ──────────────────────────────────
      case 'pricing': {
        const currencyOptions = getCurrencySelectOptions ? getCurrencySelectOptions() : [];
        const isEditing = editingOption !== null;
        return (
          <Grid container spacing={3}>
            {/* Sélecteur de devise (utilisé dans les libellés des colonnes variantes) */}
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('form.currency')}</InputLabel>
                <Select
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  label={t('form.currency')}
                  disabled
                >
                  {currencyOptions.length > 0
                    ? currencyOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))
                    : [
                        <MenuItem key="EUR" value="EUR">{t('form.currencyOptionEUR')}</MenuItem>,
                        <MenuItem key="USD" value="USD">{t('form.currencyOptionUSD')}</MenuItem>,
                        <MenuItem key="GBP" value="GBP">{t('form.currencyOptionGBP')}</MenuItem>,
                        <MenuItem key="MAD" value="MAD">{t('form.currencyOptionMAD')}</MenuItem>,
                        <MenuItem key="TND" value="TND">{t('form.currencyOptionTND')}</MenuItem>,
                        <MenuItem key="DZD" value="DZD">{t('form.currencyOptionDZD')}</MenuItem>
                      ]}
                </Select>
              </FormControl>
              {form.country_code && (
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" icon={<LocationOnIcon fontSize="small" />}
                    label={`${form.country_code} → ${form.currency}`} color="primary" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {t('form.currencyDetected')}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={7}>
              <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                {t('form.variantPriceCalculatedInfo')}
              </Alert>
            </Grid>

            {/* Message d'erreur variantes */}
            {errors.variants && (
              <Grid item xs={12} ref={(el) => { fieldRefs.current.variants = el; }}>
                <Alert severity="error">{String(errors.variants)}</Alert>
              </Grid>
            )}

            {/* ── Variantes ── */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label={t('form.variantsTitle')} icon={<InventoryIcon />} />
              </Divider>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('form.variantFieldsHelper')}
              </Typography>
            </Grid>

            {/* Formulaire ajout de type de variante */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                {/* Ligne 1 : sélection type */}
                <Typography variant="subtitle2" gutterBottom>
                  {isEditing
                    ? t('form.editOptionHeader', { value: newOption.value, typeLabel: PRODUCT_VARIANT_TYPES.find(vt => vt.value === variants[editingOption.vIdx]?.type)?.label || '' })
                    : t('form.addVariantHeader')}
                </Typography>
                {!isEditing && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel>{t('form.variantTypeLabel')}</InputLabel>
                      <Select value={newVariantType} onChange={(e) => setNewVariantType(e.target.value)}
                        label={t('form.variantTypeLabel')}>
                        {PRODUCT_VARIANT_TYPES.map((vt) => (
                          <MenuItem key={vt.value} value={vt.value}>{vt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" startIcon={<AddIcon />} size="small"
                      disabled={!newVariantType || variants.some((v) => v.type === newVariantType)}
                      onClick={() => {
                        if (!newVariantType || variants.some((v) => v.type === newVariantType)) return;
                        setVariants((prev) => [...prev, { type: newVariantType, options: [] }]);
                        setNewVariantType('');
                      }}>
                      {t('form.addVariantType')}
                    </Button>
                  </Box>
                )}

                {/* Formulaire option (ajout ou édition) */}
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {isEditing
                    ? t('form.editOptionText')
                    : t('form.addOptionText')}
                </Typography>
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item xs={6} sm={2}>
                    <TextField size="small" fullWidth label={t('form.variantValue') + ' *'} value={newOption.value}
                      onChange={(e) => setNewOption((o) => ({ ...o, value: e.target.value }))}
                      placeholder={t('form.variantValuePlaceholder')} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField size="small" fullWidth label={`${t('form.price')} (${form.currency}) *`} type="number"
                      value={newOption.price}
                      onChange={(e) => setNewOption((o) => ({ ...o, price: e.target.value }))}
                      inputProps={{ min: 0, step: '0.01' }} placeholder="15.99" />
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <TextField size="small" fullWidth label={t('form.quantity')} type="number"
                      value={newOption.quantity}
                      onChange={(e) => setNewOption((o) => ({ ...o, quantity: e.target.value }))}
                      inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <TextField size="small" fullWidth label={t('form.weight') + ' *'} type="number"
                      value={newOption.weight_g}
                      onChange={(e) => setNewOption((o) => ({ ...o, weight_g: e.target.value }))}
                      inputProps={{ min: 0 }} placeholder="500" />
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <TextField size="small" fullWidth label={t('form.tableHeaderLength') + ' *'} type="number"
                      value={newOption.length_cm}
                      onChange={(e) => setNewOption((o) => ({ ...o, length_cm: e.target.value }))}
                      inputProps={{ min: 0 }} placeholder="30" />
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <TextField size="small" fullWidth label={t('form.tableHeaderWidth') + ' *'} type="number"
                      value={newOption.width_cm}
                      onChange={(e) => setNewOption((o) => ({ ...o, width_cm: e.target.value }))}
                      inputProps={{ min: 0 }} placeholder="20" />
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <TextField size="small" fullWidth label={t('form.tableHeaderHeight') + ' *'} type="number"
                      value={newOption.height_cm}
                      onChange={(e) => setNewOption((o) => ({ ...o, height_cm: e.target.value }))}
                      inputProps={{ min: 0 }} placeholder="10" />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField size="small" fullWidth label={t('form.variantOptionSku')}
                      value={newOption.sku}
                      onChange={(e) => setNewOption((o) => ({ ...o, sku: e.target.value }))}
                      placeholder={t('form.variantSkuPlaceholder')} />
                  </Grid>
                  {/* Bouton Annuler (mode édition uniquement) */}
                  {isEditing && (
                    <Grid item xs={12} sm={1}>
                      <Button size="small" variant="outlined" color="inherit"
                        onClick={() => {
                          setEditingOption(null);
                          setNewOption({ value: '', price: '', weight_g: '', length_cm: '', width_cm: '', height_cm: '', quantity: '', sku: '' });
                        }}>
                        {t('form.cancel')}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Liste des variantes */}
            {variants.map((variant, vIdx) => {
              const typeLabel = PRODUCT_VARIANT_TYPES.find((vt) => vt.value === variant.type)?.label || variant.type;
              const isThisEditing = isEditing && editingOption.vIdx === vIdx;
              return (
                <Grid item xs={12} key={vIdx}>
                  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: isThisEditing ? 'primary.main' : undefined, borderWidth: isThisEditing ? 2 : 1 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>{typeLabel}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {/* Bouton + Option (hors mode édition) ou Valider (mode édition sur ce type) */}
                          {isEditing && isThisEditing ? (
                            <Button size="small" variant="contained" color="success"
                              disabled={
                                !newOption.value.trim() || !newOption.price || !newOption.quantity ||
                                !newOption.weight_g || !newOption.length_cm || !newOption.width_cm || !newOption.height_cm
                              }
                              onClick={() => {
                                const updated = {
                                  value: newOption.value.trim(),
                                  price: newOption.price,
                                  weight_g: Number(newOption.weight_g),
                                  length_cm: Number(newOption.length_cm),
                                  width_cm: Number(newOption.width_cm),
                                  height_cm: Number(newOption.height_cm),
                                  quantity: Number(newOption.quantity),
                                  ...(newOption.sku.trim() ? { sku: newOption.sku.trim() } : {})
                                };
                                setVariants((prev) =>
                                  prev.map((v, i) =>
                                    i === vIdx
                                      ? { ...v, options: v.options.map((o, j) => j === editingOption.oIdx ? updated : o) }
                                      : v
                                  )
                                );
                                setEditingOption(null);
                                setNewOption({ value: '', price: '', weight_g: '', length_cm: '', width_cm: '', height_cm: '', quantity: '', sku: '' });
                              }}>
                              ✓ Valider
                            </Button>
                          ) : (
                            <Button size="small" variant="contained" startIcon={<AddIcon />}
                              disabled={
                                isEditing || // désactivé si on est en train d'éditer un autre type
                                !newOption.value.trim() || !newOption.price || !newOption.quantity ||
                                !newOption.weight_g || !newOption.length_cm || !newOption.width_cm || !newOption.height_cm
                              }
                              onClick={() => {
                                const opt = {
                                  value: newOption.value.trim(),
                                  price: newOption.price,
                                  weight_g: Number(newOption.weight_g),
                                  length_cm: Number(newOption.length_cm),
                                  width_cm: Number(newOption.width_cm),
                                  height_cm: Number(newOption.height_cm),
                                  quantity: Number(newOption.quantity),
                                  ...(newOption.sku.trim() ? { sku: newOption.sku.trim() } : {})
                                };
                                setVariants((prev) =>
                                  prev.map((v, i) => i === vIdx ? { ...v, options: [...v.options, opt] } : v)
                                );
                                setNewOption({ value: '', price: '', weight_g: '', length_cm: '', width_cm: '', height_cm: '', quantity: '', sku: '' });
                              }}>
                              + Option
                            </Button>
                          )}
                          <IconButton size="small" color="error"
                            onClick={() => {
                              setVariants((prev) => prev.filter((_, i) => i !== vIdx));
                              if (isThisEditing) { setEditingOption(null); setNewOption({ value: '', price: '', weight_g: '', length_cm: '', width_cm: '', height_cm: '', quantity: '', sku: '' }); }
                            }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Tableau des options */}
                      {variant.options.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('form.tableHeaderValue')}</TableCell>
                              <TableCell>{t('form.tableHeaderPrice')} ({form.currency})</TableCell>
                              <TableCell>{t('form.tableHeaderQty')}</TableCell>
                              <TableCell>{t('form.tableHeaderWeight')}</TableCell>
                              <TableCell>{t('form.tableHeaderLength')}</TableCell>
                              <TableCell>{t('form.tableHeaderWidth')}</TableCell>
                              <TableCell>{t('form.tableHeaderHeight')}</TableCell>
                              <TableCell>{t('form.tableHeaderSku')}</TableCell>
                              <TableCell align="right">{t('form.tableHeaderActions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {variant.options.map((opt, oIdx) => {
                              const isThisRow = isEditing && editingOption.vIdx === vIdx && editingOption.oIdx === oIdx;
                              return (
                                <TableRow key={oIdx} sx={{ bgcolor: isThisRow ? 'primary.50' : undefined }}>
                                  <TableCell>{opt.value}</TableCell>
                                  <TableCell>
                                    {opt.price !== undefined && opt.price !== ''
                                      ? `${Number(opt.price).toFixed(2)}`
                                      : opt.price_cents !== undefined
                                        ? `${(opt.price_cents / 100).toFixed(2)}`
                                        : '—'}
                                  </TableCell>
                                  <TableCell>{opt.quantity}</TableCell>
                                  <TableCell>{opt.weight_g}</TableCell>
                                  <TableCell>{opt.length_cm}</TableCell>
                                  <TableCell>{opt.width_cm}</TableCell>
                                  <TableCell>{opt.height_cm}</TableCell>
                                  <TableCell>{opt.sku || '—'}</TableCell>
                                  <TableCell align="right">
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                      {/* Bouton éditer */}
                                      <Tooltip title={t('form.editOptionTooltip')}>
                                        <IconButton size="small" color="primary"
                                          disabled={isEditing && !isThisRow}
                                          onClick={() => {
                                            if (isThisRow) {
                                              // Annuler l'édition
                                              setEditingOption(null);
                                              setNewOption({ value: '', price: '', weight_g: '', length_cm: '', width_cm: '', height_cm: '', quantity: '', sku: '' });
                                            } else {
                                              // Charger l'option dans le formulaire
                                              setEditingOption({ vIdx, oIdx });
                                              setNewOption({
                                                value: opt.value || '',
                                                price: opt.price !== undefined && opt.price !== ''
                                                  ? String(opt.price)
                                                  : opt.price_cents !== undefined
                                                    ? (opt.price_cents / 100).toFixed(2)
                                                    : '',
                                                weight_g: String(opt.weight_g ?? ''),
                                                length_cm: String(opt.length_cm ?? ''),
                                                width_cm: String(opt.width_cm ?? ''),
                                                height_cm: String(opt.height_cm ?? ''),
                                                quantity: String(opt.quantity ?? ''),
                                                sku: opt.sku || ''
                                              });
                                            }
                                          }}>
                                          {isThisRow
                                            ? <span style={{ fontSize: 12, fontWeight: 700 }}>✕</span>
                                            : <span style={{ fontSize: 14 }}>✎</span>}
                                        </IconButton>
                                      </Tooltip>
                                      {/* Bouton supprimer */}
                                      <IconButton size="small" color="error"
                                        disabled={isThisRow}
                                        onClick={() =>
                                          setVariants((prev) =>
                                            prev.map((v, i) =>
                                              i === vIdx
                                                ? { ...v, options: v.options.filter((_, j) => j !== oIdx) }
                                                : v
                                            )
                                          )
                                        }>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('form.noOptionHelper')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            {variants.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('form.noVariants')}
                </Typography>
              </Grid>
            )}
          </Grid>
        );
      }

      // ── Étape 1: Localisation ──────────────────────────────────────────────
      // ── Étape 1: Localisation ──────────────────────────────────────────────
      case 'location':
        return (
          <Grid container spacing={3}>
            {/* Message informatif en mode édition : devise immuable */}
            {isEditMode && (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    border: '1.5px solid #0288d1',
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%)',
                    p: 2.5,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <InfoIcon sx={{ color: '#0277bd', fontSize: 28, mt: 0.2, flexShrink: 0 }} />
                    <Box>
                      <AlertTitle sx={{ color: '#01579b', fontWeight: 700, fontSize: '0.95rem', mb: 0.4 }}>
                        {t('form.currencyLockedTitle')}
                      </AlertTitle>
                      <Typography variant="body2" sx={{ color: '#0277bd', lineHeight: 1.7, fontSize: '0.85rem' }}>
                        {t('form.currencyChangeRequiresNewProduct')}
                      </Typography>
                    </Box>
                  </Box>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Alert severity="info">
                {t('form.pickupLocationInfo')}</Alert>
            </Grid>
            {errors.pickup_location && (
              <Grid item xs={12} ref={currencyErrorAlertRef}>
                <Alert
                  severity="error"
                  icon={false}
                  sx={{
                    border: '2px solid #e53935',
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #ffebee 0%, #fff5f5 100%)',
                    boxShadow: '0 4px 20px rgba(211, 47, 47, 0.2)',
                    p: 2.5,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <ErrorIcon sx={{ color: '#c62828', fontSize: 32, mt: 0.3, flexShrink: 0 }} />
                    <Box>
                      <AlertTitle sx={{ color: '#c62828', fontWeight: 700, fontSize: '1rem', mb: 0.5, letterSpacing: 0.3 }}>
                        {t('form.currencyMismatchTitle')}
                      </AlertTitle>
                      <Typography variant="body2" sx={{ color: '#b71c1c', fontWeight: 500, lineHeight: 1.7 }}>
                        {String(errors.pickup_location)}
                      </Typography>
                    </Box>
                  </Box>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <ListingMap
                initialPosition={mapPosition}
                onPositionChange={handleMapPositionChange}
                height={400}
              />
            </Grid>
            {pickupLocation && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('form.pickupLocation')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {addressLabel || `${pickupLocation.lat?.toFixed(5)}, ${pickupLocation.lng?.toFixed(5)}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lat: {pickupLocation.lat?.toFixed(6)} — Lng: {pickupLocation.lng?.toFixed(6)}
                      </Typography>
                    </Box>
                    {form.country_code && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Chip
                          size="small"
                          icon={<EuroIcon fontSize="small" />}
                          label={`${t('form.detectedCurrency')} : ${form.currency}`}
                          color="success"
                          variant="filled"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {t('form.country')} : {form.country_code}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        );

      // ── Étape 3: Livraison (Grille Tarifaire Dynamique) ────────────────────────
      case 'delivery':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🚚 {t('delivery.sectionTitle')}
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliveryOptions.enabled}
                        onChange={(e) => setDeliveryOptions({ ...deliveryOptions, enabled: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={t('delivery.enableDelivery')}
                    sx={{ mb: 2 }}
                  />
                  
                  <Collapse in={deliveryOptions.enabled} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
                        🎯 <strong>{t('delivery.pricingTiersInfo', 'Définissez vos propres paliers')}</strong> : {t('delivery.pricingTiersHelp', 'Choisissez librement les quantités et les prix pour chaque tranche.')}
                      </Alert>
                      
                      {/* ─── Livraison Standard (OBLIGATOIRE) ─── */}
                      <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Typography variant="h6" fontWeight={600}>
                            📦 {t('delivery.type.standard')}
                          </Typography>
                          <Chip label={t('delivery.mandatory', 'Obligatoire')} size="small" color="error" />
                        </Box>
                        
                        {deliveryOptions.standard.pricing_tiers.map((tier, index) => (
                          <Card 
                            key={index} 
                            variant="outlined" 
                            sx={{ 
                              mb: 2, 
                              p: 2,
                              borderColor: 'primary.main',
                              borderWidth: 2,
                              background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f5e9 100%)'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Chip 
                                label={`${t('delivery.tier', 'Palier')} ${index + 1}`}
                                color="primary" 
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                              <IconButton 
                                color="error" 
                                size="small"
                                onClick={() => removeTier('standard', index)}
                                disabled={deliveryOptions.standard.pricing_tiers.length === 1}
                                title={t('delivery.removeTier', 'Supprimer ce palier')}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                            
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  label={t('delivery.minQuantity', 'Quantité minimum') + ' *'}
                                  type="number"
                                  fullWidth
                                  value={tier.min_quantity}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    updateTier('standard', index, 'min_quantity', val);
                                  }}
                                  inputProps={{ min: 1 }}
                                  helperText={index === 0 ? t('delivery.from', 'À partir de...') : t('delivery.autoCalculated', 'Calculé automatiquement')}
                                  size="small"
                                  error={tier.min_quantity < 1 || (tier.max_quantity !== null && tier.min_quantity > tier.max_quantity)}
                                  disabled={index > 0}
                                />
                              </Grid>
                              
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  label={t('delivery.maxQuantity', 'Quantité maximum')}
                                  type="number"
                                  fullWidth
                                  value={tier.max_quantity || ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? null : parseInt(e.target.value);
                                    updateTier('standard', index, 'max_quantity', val);
                                  }}
                                  placeholder={t('delivery.unlimited', '∞ Illimité')}
                                  helperText={t('delivery.emptyIsUnlimited', 'Vide = pas de limite')}
                                  inputProps={{ min: tier.min_quantity }}
                                  size="small"
                                  error={tier.max_quantity !== null && tier.max_quantity < tier.min_quantity}
                                />
                              </Grid>
                              
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label={t('delivery.tierPrice', 'Prix de livraison') + ' *'}
                                  fullWidth
                                  value={(tier.price_cents / 100).toString().replace(/^0+(?=\d)/, '')}
                                  onChange={(e) => {
                                    let inputValue = e.target.value;
                                    
                                    // Permettre uniquement chiffres, point et max 2 décimales
                                    if (inputValue === '' || /^\d*\.?\d{0,2}$/.test(inputValue)) {
                                      // Convertir en cents
                                      const priceValue = inputValue === '' ? 0 : parseFloat(inputValue);
                                      const cents = Math.round(priceValue * 100);
                                      updateTier('standard', index, 'price_cents', isNaN(cents) ? 0 : cents);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    // Formater à 2 décimales au blur
                                    const value = parseFloat(e.target.value) || 0;
                                    updateTier('standard', index, 'price_cents', Math.round(value * 100));
                                  }}
                                  InputProps={{
                                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>💰</Typography>,
                                    endAdornment: <Typography variant="caption" fontWeight={600}>{form.currency}</Typography>
                                  }}
                                  helperText={t('delivery.costForTier', 'Coût de livraison pour cette tranche')}
                                  size="small"
                                  error={tier.price_cents < 0}
                                  inputRef={index === 0 ? (el) => { fieldRefs.current.delivery_standard_tiers = el; } : undefined}
                                />
                              </Grid>
                            </Grid>
                            
                            <Box sx={{ 
                              mt: 2, 
                              p: 1.5, 
                              bgcolor: 'white', 
                              borderRadius: 1,
                              border: '1px dashed',
                              borderColor: 'primary.main'
                            }}>
                              <Typography variant="body2" fontWeight={600} color="primary">
                                📊 {t('delivery.rule', 'Règle')} : {tier.min_quantity} {tier.max_quantity ? `${t('delivery.to', 'à')} ${tier.max_quantity}` : t('delivery.andMore', 'articles et plus')}
                                {' → '}
                                <Typography component="span" variant="body2" fontWeight={700} color="success.main">
                                  {(tier.price_cents / 100).toFixed(2)} {form.currency}
                                </Typography>
                              </Typography>
                            </Box>
                          </Card>
                        ))}
                        
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => addTier('standard')}
                          variant="contained"
                          color="primary"
                          fullWidth
                          sx={{ mb: 2 }}
                          disabled={deliveryOptions.standard.pricing_tiers[deliveryOptions.standard.pricing_tiers.length - 1]?.max_quantity === null || Number(deliveryOptions.standard.pricing_tiers[deliveryOptions.standard.pricing_tiers.length - 1]?.max_quantity) === 0 || Number(deliveryOptions.standard.pricing_tiers[deliveryOptions.standard.pricing_tiers.length - 1]?.max_quantity) < Number(deliveryOptions.standard.pricing_tiers[deliveryOptions.standard.pricing_tiers.length - 1]?.min_quantity)}
                          >
                           {t('delivery.addTier', 'Ajouter un nouveau palier')}
                        </Button>
                        
                        <TextField
                          label={t('delivery.standardDays', 'Délai estimé (jours)') + ' *'}
                          type="number"
                          fullWidth
                          value={deliveryOptions.standard.days}
                          onChange={(e) => updateDeliveryDays('standard', e.target.value)}
                          inputProps={{ min: 1 }}
                          helperText={t('delivery.standardDaysHelp', 'Nombre de jours estimés pour la livraison')}
                          inputRef={(el) => { fieldRefs.current.delivery_standard_days = el; }}
                        />
                        
                        {/* Aperçu de la grille */}
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 2 }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            📋 {t('delivery.tierPreview', 'Aperçu de votre grille tarifaire')} :
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>{t('delivery.quantity', 'Quantité')}</strong></TableCell>
                                <TableCell align="right"><strong>{t('delivery.deliveryPrice', 'Prix de livraison')}</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {deliveryOptions.standard.pricing_tiers
                                .slice()
                                .sort((a, b) => a.min_quantity - b.min_quantity)
                                .map((tier, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      {tier.min_quantity} {tier.max_quantity ? `- ${tier.max_quantity}` : '+'} {t('delivery.articles', 'article(s)')}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip 
                                        label={`${(tier.price_cents / 100).toFixed(2)} ${form.currency}`}
                                        color="success"
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Paper>
                        
                        {/* Alertes de Validation */}
                        {(() => {
                          const validationErrors = validateTiers(deliveryOptions.standard.pricing_tiers);
                          if (validationErrors.length > 0) {
                            return (
                              <Alert severity="error" sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  {t('delivery.tierValidation.validationErrors', 'Erreurs de validation :')}
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {validationErrors.map((err, i) => (
                                    <li key={i}>
                                      <Typography variant="body2">
                                         {t('delivery.tierValidation.tier', 'Palier')} : {err.message}
                                      </Typography>
                                    </li>
                                  ))}
                                </ul>
                              </Alert>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                      
                      {/* ─── Livraison Express (FACULTATIF) ─── */}
                      <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.300' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              ⚡ {t('delivery.type.express')}
                            </Typography>
                            <Chip label={t('delivery.optional', 'Facultatif')} size="small" color="success" />
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={deliveryOptions.expressEnabled}
                                onChange={(e) => setDeliveryOptions(prev => ({ ...prev, expressEnabled: e.target.checked }))}
                                color="warning"
                              />
                            }
                            label={deliveryOptions.expressEnabled ? t('delivery.expressActivated', 'Activée') : t('delivery.expressDeactivated', 'Désactivée')}
                          />
                        </Box>
                        
                        <Alert severity="info" icon="💡" sx={{ mb: 2 }}>
                          {t('delivery.expressInfo', 'La livraison express est facultative. Activez-la si vous souhaitez proposer une option de livraison plus rapide à vos clients.')}
                        </Alert>
                        
                        <Collapse in={deliveryOptions.expressEnabled} timeout="auto" unmountOnExit>
                          <Box>
                            {deliveryOptions.express.pricing_tiers.map((tier, index) => (
                              <Card 
                                key={index} 
                                variant="outlined" 
                                sx={{ 
                                  mb: 2, 
                                  p: 2,
                                  borderColor: 'warning.main',
                                  borderWidth: 2,
                                  background: 'linear-gradient(135deg, #fff9e6 0%, #ffe8cc 100%)'
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                  <Chip 
                                    label={`${t('delivery.tier', 'Palier')} ${index + 1}`}
                                    color="warning" 
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                  <IconButton 
                                    color="error" 
                                    size="small"
                                    onClick={() => removeTier('express', index)}
                                    disabled={deliveryOptions.express.pricing_tiers.length === 1}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      label={t('delivery.minQuantity', 'Quantité minimum') + ' *'}
                                      type="number"
                                      fullWidth
                                      value={tier.min_quantity}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        updateTier('express', index, 'min_quantity', val);
                                      }}
                                      inputProps={{ min: 1 }}
                                      size="small"
                                      error={tier.min_quantity < 1 || (tier.max_quantity !== null && tier.min_quantity > tier.max_quantity)}
                                      disabled={index > 0}
                                      helperText={index === 0 ? t('delivery.from', 'À partir de...') : t('delivery.autoCalculated', 'Calculé automatiquement')}
                                    />
                                  </Grid>
                                  
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      label={t('delivery.maxQuantity', 'Quantité maximum')}
                                      type="number"
                                      fullWidth
                                      value={tier.max_quantity || ''}
                                      onChange={(e) => {
                                        const val = e.target.value === '' ? null : parseInt(e.target.value);
                                        updateTier('express', index, 'max_quantity', val);
                                      }}
                                      placeholder={t('delivery.unlimited', '∞ Illimité')}
                                      helperText={t('delivery.emptyIsUnlimited', 'Vide = pas de limite')}
                                      inputProps={{ min: tier.min_quantity }}
                                      size="small"
                                      error={tier.max_quantity !== null && tier.max_quantity < tier.min_quantity}
                                    />
                                  </Grid>
                                  
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      label={t('delivery.tierPrice', 'Prix de livraison') + ' *'}
                                      fullWidth
                                      value={(tier.price_cents / 100).toString().replace(/^0+(?=\d)/, '')}
                                      onChange={(e) => {
                                        let inputValue = e.target.value;
                                        
                                        // Permettre uniquement chiffres, point et max 2 décimales
                                        if (inputValue === '' || /^\d*\.?\d{0,2}$/.test(inputValue)) {
                                          // Convertir en cents
                                          const priceValue = inputValue === '' ? 0 : parseFloat(inputValue);
                                          const cents = Math.round(priceValue * 100);
                                          updateTier('express', index, 'price_cents', isNaN(cents) ? 0 : cents);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        // Formater à 2 décimales au blur
                                        const value = parseFloat(e.target.value) || 0;
                                        updateTier('express', index, 'price_cents', Math.round(value * 100));
                                      }}
                                      InputProps={{
                                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>💰</Typography>,
                                        endAdornment: <Typography variant="caption" fontWeight={600}>{form.currency}</Typography>
                                      }}
                                      helperText={t('delivery.costForTier', 'Coût de livraison pour cette tranche')}
                                      size="small"
                                      error={tier.price_cents < 0}
                                      inputRef={index === 0 ? (el) => { fieldRefs.current.delivery_express_tiers = el; } : undefined}
                                    />
                                  </Grid>
                                </Grid>
                                
                                <Box sx={{ 
                                  mt: 2, 
                                  p: 1.5, 
                                  bgcolor: 'white', 
                                  borderRadius: 1,
                                  border: '1px dashed',
                                  borderColor: 'warning.main'
                                }}>
                                  <Typography variant="body2" fontWeight={600} color="warning.dark">
                                    📊 {t('delivery.rule', 'Règle')} : {tier.min_quantity} {tier.max_quantity ? `${t('delivery.to', 'à')} ${tier.max_quantity}` : t('delivery.andMore', 'articles et plus')}
                                    {' → '}
                                    <Typography component="span" variant="body2" fontWeight={700} color="warning.dark">
                                      {(tier.price_cents / 100).toFixed(2)} {form.currency}
                                    </Typography>
                                  </Typography>
                                </Box>
                              </Card>
                            ))}
                            
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => addTier('express')}
                              variant="contained"
                              color="warning"
                              fullWidth
                              sx={{ mb: 2 }}
                              disabled={deliveryOptions.express.pricing_tiers[deliveryOptions.express.pricing_tiers.length - 1]?.max_quantity === null || Number(deliveryOptions.express.pricing_tiers[deliveryOptions.express.pricing_tiers.length - 1]?.max_quantity) === 0 || Number(deliveryOptions.express.pricing_tiers[deliveryOptions.express.pricing_tiers.length - 1]?.max_quantity) < Number(deliveryOptions.express.pricing_tiers[deliveryOptions.express.pricing_tiers.length - 1]?.min_quantity)}
                              >
                              {t('delivery.addTier', 'Ajouter un nouveau palier')}
                            </Button>
                            
                            <TextField
                              label={t('delivery.expressDays', 'Délai estimé (jours)') + ' *'}
                              type="number"
                              fullWidth
                              value={deliveryOptions.express.days}
                              onChange={(e) => updateDeliveryDays('express', e.target.value)}
                              inputProps={{ min: 1 }}
                              helperText={t('delivery.expressDaysHelp', 'Nombre de jours estimés pour la livraison express')}
                              inputRef={(el) => { fieldRefs.current.delivery_express_days = el; }}
                            />
                            
                            {/* Alertes de Validation pour Express */}
                            {(() => {
                              const validationErrors = validateTiers(deliveryOptions.express.pricing_tiers);
                              if (validationErrors.length > 0) {
                                return (
                                  <Alert severity="error" sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                      {t('delivery.tierValidation.validationErrors', 'Erreurs de validation :')}
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                      {validationErrors.map((err, i) => (
                                        <li key={i}>
                                          <Typography variant="body2">
                                            {t('delivery.tierValidation.tier', 'Palier')} : {err.message}
                                          </Typography>
                                        </li>
                                      ))}
                                    </ul>
                                  </Alert>
                                );
                              }
                              return null;
                            })()}
                            
                            {/* Preview de la grille tarifaire Express */}
                            <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: '2px solid', borderColor: 'warning.main' }}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="warning.dark">
                                📋 {t('delivery.tierPreview', 'Aperçu de votre grille tarifaire')}
                              </Typography>
                              <Box component="table" sx={{ width: '100%', mt: 1 }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #ff9800' }}>
                                      {t('delivery.quantityRange', 'Quantité')}
                                    </th>
                                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #ff9800' }}>
                                      {t('delivery.deliveryPrice', 'Prix de livraison')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deliveryOptions.express.pricing_tiers.map((tier, idx) => (
                                    <tr key={idx}>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        {tier.min_quantity} {tier.max_quantity ? `- ${tier.max_quantity}` : '+'} {t('delivery.articles', 'article(s)')}
                                      </td>
                                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #eee' }}>
                                        {(tier.price_cents / 100).toFixed(2)} {form.currency}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                      
                      <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                        {t('delivery.availabilityInfo', 'Les champs de disponibilité (temps de préparation, durée des créneaux, capacité) s\'appliquent aussi bien au retrait qu\'à la livraison.')}
                      </Alert>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      // ── Étape 4: Disponibilités ────────────────────────────────────────────
      case 'availability':
        return (
          <Grid container spacing={3}>
            {/* Paramètres de préparation et durée créneau */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('form.preparationTime')}</InputLabel>
                <Select
                  value={form.preparation_time_minutes}
                  onChange={(e) => setForm((f) => ({ ...f, preparation_time_minutes: e.target.value }))}
                  label={t('form.preparationTime')}
                >
                  {PREP_TIME_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('form.preparationTimeHelper')}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('form.slotDuration')}</InputLabel>
                <Select
                  value={form.default_slot_duration_minutes}
                  onChange={(e) => setForm((f) => ({ ...f, default_slot_duration_minutes: e.target.value }))}
                  label={t('form.slotDuration')}
                >
                  {SLOT_DURATION_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('form.slotDuration')}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('form.slotClientCapacity')}</InputLabel>
                <Select
                  value={form.slot_client_capacity}
                  onChange={(e) => setForm((f) => ({ ...f, slot_client_capacity: Number(e.target.value) }))}
                  label={t('form.slotClientCapacity')}
                >
                  {[1, 2, 3, 5, 10, 15, 20, 30, 50].map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('form.slotClientCapacityHelper')}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider>
                <Chip label={t('slots.add')} icon={<ScheduleIcon />} />
              </Divider>
            </Grid>

            {/* ── Planning hebdomadaire (Lundi → Dimanche) ─────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }}>
                <Chip label={t('form.weeklySchedule')} icon={<ScheduleIcon />} />
              </Divider>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('form.weeklyScheduleHelper')}
              </Typography>
              <Grid container spacing={1}>
                {WEEK_DAYS.map(({ day, label }) => {
                  const entry = openingHours.find((h) => h.day === day);
                  const enabled = Boolean(entry);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={day}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: enabled ? 'primary.main' : 'divider', borderWidth: enabled ? 2 : 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: enabled ? 1 : 0 }}>
                          <Typography variant="body2" fontWeight={600}>{label}</Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={enabled}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setOpeningHours((prev) => [...prev, { day, from: '09:00', to: '18:00' }]);
                                  } else {
                                    setOpeningHours((prev) => prev.filter((h) => h.day !== day));
                                  }
                                }}
                              />
                            }
                            label=""
                            sx={{ ml: 0, mr: 0 }}
                          />
                        </Box>
                        {enabled && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              type="time"
                              label={t('form.dayTimeFrom')}
                              value={entry.from}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) =>
                                setOpeningHours((prev) =>
                                  prev.map((h) => h.day === day ? { ...h, from: e.target.value } : h)
                                )
                              }
                              sx={{ flex: 1 }}
                            />
                            <TextField
                              size="small"
                              type="time"
                              label={t('form.dayTimeTo')}
                              value={entry.to}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) =>
                                setOpeningHours((prev) =>
                                  prev.map((h) => h.day === day ? { ...h, to: e.target.value } : h)
                                )
                              }
                              sx={{ flex: 1 }}
                            />
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            {/* ── Périodes d'indisponibilité (congés, vacances…) ─────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label={t('form.unavailability')} icon={<ScheduleIcon />} />
              </Divider>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('form.unavailabilityHelper')}
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 3, 
                  mb: 2, 
                  borderRadius: 2,
                  backgroundColor: 'background.default',
                  border: '2px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 16px rgba(98, 0, 238, 0.08)'
                  }
                }}
              >
                {/* Calendrier inspiré du gestionnaire de disponibilités */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                    <CalendarTodayIcon sx={{ mr: 1 }} />
                    {t('form.unavailabilityCalendarTitle', { defaultValue: 'Calendrier des indisponibilités' })}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <IconButton onClick={() => setUnavailabilityCalendarMonth((prev) => subMonths(prev, 1))}>
                      <ChevronLeftIcon />
                    </IconButton>

                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {format(unavailabilityCalendarMonth, 'MMMM yyyy', { locale: calendarLocale })}
                    </Typography>

                    <IconButton onClick={() => setUnavailabilityCalendarMonth((prev) => addMonths(prev, 1))}>
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', backgroundColor: 'background.paper' }}>
                    <Grid container>
                      {unavailabilityWeekdayLabels.map((dayLabel) => (
                        <Grid item xs={12 / 7} key={dayLabel}>
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: 1,
                              fontWeight: 700,
                              backgroundColor: 'grey.100',
                              borderRight: 1,
                              borderColor: 'divider',
                              fontSize: '0.8rem'
                            }}
                          >
                            {dayLabel}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Grid container>
                      {unavailabilityCalendarDays.map((day, index) => {
                        const isCurrentMonth = isSameMonth(day, unavailabilityCalendarMonth);
                        const isPastDay = isBefore(day, today);
                        const isSelected = selectedUnavailabilityStart && (
                          selectedUnavailabilityEnd
                            ? isWithinInterval(day, { start: selectedUnavailabilityStart, end: selectedUnavailabilityEnd })
                            : isSameDay(day, selectedUnavailabilityStart)
                        );
                        const isRangeEdge = (selectedUnavailabilityStart && isSameDay(day, selectedUnavailabilityStart))
                          || (selectedUnavailabilityEnd && isSameDay(day, selectedUnavailabilityEnd));
                        const hasExistingUnavailability = isExistingUnavailabilityDay(day);

                        let backgroundColor = 'common.white';
                        let textColor = isCurrentMonth ? 'text.primary' : 'text.disabled';
                        let cursor = 'pointer';
                        let opacity = isCurrentMonth ? 1 : 0.45;

                        if (isPastDay) {
                          backgroundColor = 'grey.200';
                          textColor = 'text.disabled';
                          cursor = 'not-allowed';
                          opacity = 0.8;
                        } else if (isSelected) {
                          backgroundColor = isRangeEdge ? 'primary.main' : 'primary.light';
                          textColor = 'primary.contrastText';
                        } else if (hasExistingUnavailability) {
                          backgroundColor = 'error.light';
                          textColor = 'common.white';
                          cursor = 'not-allowed';
                          opacity = 0.95;
                        }

                        return (
                          <Grid item xs={12 / 7} key={`${format(day, 'yyyy-MM-dd')}-${index}`}>
                            <Box
                              onClick={() => handleUnavailabilityDateClick(day)}
                              sx={{
                                height: 58,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor,
                                color: textColor,
                                borderRight: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                cursor,
                                opacity,
                                fontWeight: isSameDay(day, today) ? 700 : 500,
                                position: 'relative',
                                transition: 'background-color 0.2s ease, transform 0.2s ease',
                                '&:hover': !isPastDay ? {
                                  backgroundColor: isSelected ? backgroundColor : 'grey.100'
                                } : undefined
                              }}
                            >
                              {format(day, 'd')}
                              {hasExistingUnavailability && !isSelected && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 6,
                                    right: 6,
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    backgroundColor: 'error.main'
                                  }}
                                />
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 16, height: 16, backgroundColor: 'primary.light', border: 1, borderColor: 'divider' }} />
                      <Typography variant="caption">{t('form.unavailabilityLegendSelected', { defaultValue: 'Sélection' })}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 16, height: 16, backgroundColor: 'error.light', border: 1, borderColor: 'divider' }} />
                      <Typography variant="caption">{t('form.unavailabilityLegendExisting')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 16, height: 16, backgroundColor: 'grey.200', border: 1, borderColor: 'divider' }} />
                      <Typography variant="caption">{t('form.unavailabilityLegendPast')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 16, height: 16, backgroundColor: 'common.white', border: 1, borderColor: 'divider' }} />
                      <Typography variant="caption">{t('form.unavailabilityLegendFree')}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                      {selectedUnavailabilityDayCount > 0
                        ? t('form.unavailabilitySelectedDays', { count: selectedUnavailabilityDayCount })
                        : t('form.unavailabilitySelectionHint')}
                    </Typography>
                    {(selectedUnavailabilityStart || selectedUnavailabilityEnd) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {selectedUnavailabilityStart ? format(selectedUnavailabilityStart, 'dd/MM/yyyy') : '--'}
                        {' → '}
                        {selectedUnavailabilityEnd ? format(selectedUnavailabilityEnd, 'dd/MM/yyyy') : t('form.unavailabilitySelectionEnd')}
                      </Typography>
                    )}
                    {(selectedUnavailabilityStart || selectedUnavailabilityEnd) && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={clearUnavailabilitySelection}
                        sx={{ mt: 1, px: 0, textTransform: 'none' }}
                      >
                        {t('form.unavailabilityClearSelection')}
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Raison d'indisponibilité */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    {t('form.unavailabilityReasonLabel')}
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('form.unavailabilityReason')}
                    value={newUnavailability.reason}
                    onChange={(e) => setNewUnavailability((u) => ({ ...u, reason: e.target.value }))}
                    placeholder={t('form.unavailabilityReasonPlaceholder')}
                    multiline
                    rows={2}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        '&:hover': { borderColor: 'primary.main' },
                        '&.Mui-focused': {
                          borderColor: 'primary.main',
                          boxShadow: '0 0 0 3px rgba(98, 0, 238, 0.1)'
                        }
                      }
                    }}
                  />
                </Box>

                {/* Bouton d'ajout */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    disabled={!newUnavailability.available_from || !newUnavailability.available_to}
                    onClick={() => {
                      if (!newUnavailability.available_from || !newUnavailability.available_to) return;
                      
                      // Check for overlaps with existing periods
                      const { hasOverlap, conflictingPeriods } = checkUnavailabilityOverlap(
                        newUnavailability.available_from,
                        newUnavailability.available_to,
                        unavailabilityPeriods
                      );
                      
                      if (hasOverlap) {
                        const conflictDates = conflictingPeriods
                          .map((p) => `${p.available_from} → ${p.available_to}`)
                          .join(', ');
                        showNotification(
                          t('form.unavailabilityOverlapError', { defaultValue: `${conflictDates}` }),
                          'error'
                        );
                        return;
                      }
                      
                      setUnavailabilityPeriods((prev) => [...prev, { ...newUnavailability, _local: true }]);
                      setUnavailabilitySelectionStart(null);
                      setNewUnavailability({ available_from: '', available_to: '', reason: '' });
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 3,
                      '&:hover': {
                        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                        boxShadow: '0 4px 12px rgba(98, 0, 238, 0.3)'
                      }
                    }}
                  >
                    {t('form.unavailabilityAdd')}
                  </Button>
                </Box>
              </Paper>

              {unavailabilityPeriods.filter(p => !p._deleted).length > 0 && (
                <Grid container spacing={1}>
                  {unavailabilityPeriods.map((period, pIdx) => {
                    if (period._deleted) return null;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={pIdx}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: 'warning.main', borderWidth: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {period.available_from} → {period.available_to}
                              </Typography>
                              {period.reason && (
                                <Typography variant="caption" color="text.secondary">{period.reason}</Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setUnavailabilityPeriods((prev) =>
                                  prev.map((p, i) => i === pIdx ? { ...p, _deleted: true } : p)
                                );
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Grid>
          </Grid>
        );

      // ── Étape 4: Images ────────────────────────────────────────────────────
      case 'images':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                {t('form.images')}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={images.length >= 10}
              >
                {t('form.images')}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageSelect}
                />
              </Button>
              <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                {t('form.imageFormatsHint', { count: images.length, max: 10 })}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {images.map((img, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Card sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 140,
                          overflow: 'hidden',
                          borderRadius: '4px 4px 0 0',
                          border: i === 0 ? '3px solid #03dac4' : '3px solid transparent',
                          boxShadow: i === 0 ? '0 0 0 2px rgba(3,218,196,0.3)' : 'none',
                          position: 'relative',
                        }}
                      >
                        <img
                          src={img.preview}
                          alt={`Image ${i + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        {i === 0 && (
                          <Box sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(3, 218, 196, 0.9)',
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textAlign: 'center',
                            py: 0.3,
                            letterSpacing: 0.3
                          }}>
                            {t('listing.form.mainPhoto', 'Photo principale')}
                          </Box>
                        )}
                      </Box>

                      {/* Boutons d'action */}
                      <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, justifyContent: 'center' }}>
                        {i !== 0 && (
                          <IconButton
                            size="small"
                            onClick={() => handleSetMainImage(i)}
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              border: '1px solid #e0e0e0',
                              '&:hover': { backgroundColor: '#fff', borderColor: '#03dac4' }
                            }}
                            title={t('listing.form.setAsMain', 'Définir comme photo principale')}
                          >
                            <StarBorderIcon sx={{ fontSize: 16, color: '#03dac4' }} />
                          </IconButton>
                        )}
                        {i === 0 && (
                          <IconButton
                            size="small"
                            disabled
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: 'rgba(3, 218, 196, 0.1)',
                              border: '1px solid #03dac4',
                            }}
                            title={t('listing.form.isMainPhoto', 'Photo principale')}
                          >
                            <StarIcon sx={{ fontSize: 16, color: '#03dac4' }} />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleImageRemove(i)}
                          sx={{
                            width: 28,
                            height: 28,
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            border: '1px solid #e0e0e0',
                            '&:hover': { backgroundColor: 'error.main', borderColor: 'error.main', color: '#fff' }
                          }}
                          title={t('listing.form.removeImage', 'Supprimer')}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
                {images.length === 0 && (
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}
                    >
                      <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                      <Typography variant="body2">{t('detail.noImage')}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  // ── Résumé étapes ──────────────────────────────────────────────────────────
  const isLastStep = activeStep === STEPS.length - 1;

  // 🆕 Fonction pour obtenir le badge du statut
  const getStatusBadge = () => {
    const statusConfig = {
      'draft': { bg: '#e0e0e0', text: '#424242', icon: '📝', label: t('productStatus.draft', { defaultValue: 'Brouillon' }) },
      'pending': { bg: '#fff3cd', text: '#856404', icon: '⏳', label: t('productStatus.pending') },
      'active': { bg: '#d4edda', text: '#155724', icon: '✅', label: t('productStatus.active') },
      'paused': { bg: '#e2e3e5', text: '#383d41', icon: '⏸️', label: t('productStatus.paused') },
      'rejected': { bg: '#f8d7da', text: '#721c24', icon: '❌', label: t('productStatus.rejected') },
      'archived': { bg: '#d1ecf1', text: '#0c5460', icon: '📦', label: t('productStatus.archived') },
      'disabled': { bg: '#f5f5f5', text: '#6b6b6b', icon: '🚫', label: t('productStatus.disabled') }
    };
    return statusConfig[productStatus] || null;
  };

  return (
    <Box sx={{ 
      maxWidth: 900, 
      mx: 'auto', 
      p: { xs: 2, sm: 3 },
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      {/* Overlay de chargement pendant la soumission */}
      {submitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 }
            }
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 3,
              padding: 5,
              boxShadow: '0 8px 40px rgba(98, 0, 238, 0.3)',
              textAlign: 'center',
              minWidth: 350,
              maxWidth: 500,
              border: '2px solid #6200ee'
            }}
          >
            <CircularProgress 
              size={70} 
              thickness={4} 
              sx={{
                color: '#6200ee',
                mb: 3
              }}
            />
            
            <Box mt={3}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  mb: 2
                }}
              >
                {submissionMessage}
              </Typography>
              
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 3, mt: 2, fontSize: '0.95rem' }}
              >
                {t('form.overlay.pleaseWait', 'Veuillez patienter...')}
              </Typography>
              
              <LinearProgress 
                sx={{ 
                  width: '100%',
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(98, 0, 238, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                    borderRadius: 4
                  }
                }} 
              />
              
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  mt: 3, 
                  display: 'block', 
                  fontStyle: 'italic',
                  fontSize: '0.85rem'
                }}
              >
                ⚠️ {t('form.overlay.doNotClose', 'Ne fermez pas cette page')}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Bouton Retour — hors bannière, extrême gauche */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/my-products')}
          variant="outlined"
          sx={{
            borderColor: '#6200ee',
            color: '#6200ee',
            fontWeight: 'medium',
            px: { xs: 2, sm: 3 },
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: '0 2px 4px rgba(98, 0, 238, 0.1)',
            '&:hover': {
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              color: 'white',
              borderColor: 'transparent',
              boxShadow: '0 4px 8px rgba(98, 0, 238, 0.3)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('myProducts.returnToList')}
        </Button>
      </Box>

      {/* Bannière : titre à gauche, badge statut à droite */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ m: 0 }}>
          {isEditMode ? t('detail.editProduct') : t('myProducts.createNew')}
        </Typography>

        {/* Badge de statut — extrême droite */}
        {isEditMode && productStatus && (
          <Chip
            label={getStatusBadge()?.label || productStatus}
            icon={
              productStatus === 'active'   ? <CheckCircleIcon /> :
              productStatus === 'pending'  ? <InfoIcon /> :
              productStatus === 'rejected' ? <ErrorIcon /> :
              productStatus === 'paused'   ? <PauseCircleIcon /> :
              productStatus === 'archived' ? <ArchiveIcon /> :
              productStatus === 'disabled' ? <BlockIcon /> :
              <InfoIcon />
            }
            sx={{
              fontSize: '1rem',
              fontWeight: 'bold',
              height: 'auto',
              py: 1,
              px: 2,
              backgroundColor:
                productStatus === 'active'   ? '#4caf50' :
                productStatus === 'pending'  ? '#ff9800' :
                productStatus === 'rejected' ? '#f44336' :
                productStatus === 'paused'   ? '#616161' :
                productStatus === 'archived' ? '#0c5460' :
                productStatus === 'disabled' ? '#616161' :
                '#9e9e9e',
              color: 'white',
              '& .MuiChip-icon': { color: 'white' },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
            }}
          />
        )}
      </Box>

      {/* 🆕 Affichage de la raison de rejet / archivage */}
      {(productStatus === 'rejected' || productStatus === 'archived') && rejectionReason && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 2, border: '1px solid #ff9800' }}
          onClose={() => setRejectionReason('')}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {productStatus === 'rejected' ? `❌ ${t('productForm.rejectionReasonTitle')} :` : `📦 ${t('productForm.archivingReasonTitle')} :`}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {String(rejectionReason)}
          </Typography>
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
        {STEPS.map((key, i) => (
          <Step key={key} completed={i < activeStep}>
            <StepLabel
              optional={
                i === STEPS.indexOf('availability') ? (
                  <Typography variant="caption">{t('steps.availability')}</Typography>
                ) : null
              }
            >
              {STEP_LABELS[key]}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3, mb: 3 }}>
        {renderStep()}
      </Paper>

      {/* Barre de navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
        >
          {t('form.back')}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Soumettre pour approbation (dernier step) */}
          {(isLastStep || productId) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handlePublish}
              disabled={loading || hasErrorsInCurrentStep()}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              {isEditMode ? t('form.save') : t('form.submit')}
            </Button>
          )}

          {/* Suivant */}
          {!isLastStep && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={loading}
            >
              {t('form.next')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
